import React, { useEffect, useState } from "react";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useBlobUrl, openAsBlob } from "../hooks/useBlobUrl";
import { useLocation, useNavigate } from "react-router-dom";
import {
  analyticsService,
  type SellerAnalyticsEvent,
  type SellerAnalyticsResponse,
  type SellerDashboardResponse,
} from "../features/analytics/service";
import { dealsService } from "../features/deals/service";
import { dealCollection, dealId, type Deal, type GalleryItem } from "../features/deals/types";
import { describeDealError } from "../features/deals/errorMessages";
import { hasStructuredPrice } from "../features/deals/priceFallback";
import { webinarsService } from "../features/webinars/service";
import type { WebinarCardResponse } from "../features/webinars/types";
import { contactsService } from "../features/contacts/service";
import type { BusinessContactResponse } from "../features/contacts/types";
import { useAuth } from "../context/AuthContext";
import { useBackgroundJobs } from "../lib/notifications/useBackgroundJobs";
import { env } from "../config/env";
import { formatISTDateTime, formatISTMonthDayShort } from "../utils/dateUtils";
import { recommendationsService } from "../features/recommendations/service";
import type { SellerScore, SellerPreferenceStatus } from "../features/recommendations/types";
import { newsService } from "../features/news/service";
import type { NewsArticle } from "../features/news/service";
import { useCountries } from "../features/geo/hooks";

/** Resolve image URL — relative /uploads/ paths are served from the backend. */
function resolveImageUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("/uploads/")) return `${env.apiBaseUrl}${url}`;
  return url;
}

// Mirrors backend GALLERY_ATTACHMENT_ALLOWED_CONTENT_TYPES (backend/app/core/config.py).
const GALLERY_ACCEPTED_TYPES = [
  "image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv", "text/plain",
];
const GALLERY_ACCEPT_ATTR = GALLERY_ACCEPTED_TYPES.join(",");

function isVideoMediaUrl(url?: string): boolean {
  if (!url) return false;
  return (
    url.match(/\.(mp4|webm|mov|avi)(\?|$)/i) !== null ||
    url.includes("/videos/") ||
    url.includes("video/upload")
  );
}

/** Small colored status badge for a seller's own gallery thumbnail (pending/approved/rejected). */
function GalleryStatusBadge({ item }: { item: GalleryItem | undefined }) {
  if (!item || item.status === "approved") return null;
  const isPending = item.status === "pending";
  const title = item.status === "rejected" ? `rejected: ${item.rejection_reason || "no reason given"}` : "pending review";
  return (
    <span
      title={title}
      className={`absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full border border-white ${
        isPending ? "bg-amber-500" : "bg-red-500"
      }`}
    />
  );
}

function galleryItemForUrl(item: Deal, url: string): GalleryItem | undefined {
  return (item.media?.gallery ?? []).find((g) => resolveImageUrl(g.key) === url || g.key === url);
}

function previewImagesForItem(item: Deal): string[] {
  const media = item.media as Record<string, unknown> | undefined;
  const imageClean = (media?.image_clean as string | undefined) || null;
  const primary = item.media?.image ? [item.media.image] : [];
  const clean = imageClean && imageClean !== item.media?.image ? [imageClean] : [];
  const gallery = (item.media?.gallery ?? []).map((g) => g.key).filter((u) => !isVideoMediaUrl(u));
  const all = [...new Set([...primary, ...clean, ...gallery])];
  return all
    .map((url) => resolveImageUrl(url))
    .filter((url): url is string => Boolean(url));
}

function galleryVideosForItem(item: Deal): string[] {
  const primary = item.media?.video_url ? [item.media.video_url] : [];
  const gallery = (item.media?.gallery ?? []).map((g) => g.key).filter((u) => isVideoMediaUrl(u));
  const all = [...new Set([...primary, ...gallery])];
  return all
    .map((url) => {
      if (!url) return undefined;
      if (url.startsWith("http://") || url.startsWith("https://")) return url;
      return `${env.apiBaseUrl}${url}`;
    })
    .filter((url): url is string => Boolean(url));
}

function itemVideoUrl(item: Deal): string | undefined {
  if (!item.media?.video_url) return undefined;
  if (item.media.video_url.startsWith("http://") || item.media.video_url.startsWith("https://")) {
    return item.media.video_url;
  }
  return `${env.apiBaseUrl}${item.media.video_url}`;
}

function isVideoItem(item: Deal): boolean {
  return (
    item.item_type === "FLYER_VIDEO" ||
    isVideoMediaUrl(item.media?.image ?? undefined) ||
    isVideoMediaUrl(item.media?.video_url ?? undefined)
  );
}

function getItemLocations(item: Deal): { cities: string[]; states: string[]; countries: string[] } {
  const isPresent = (value: string | undefined): value is string => Boolean(value);
  const cities = Array.from(
    new Set(
      [
        ...(item.destination?.cities || []),
      ].filter(isPresent)
    )
  );
  const states = Array.from(
    new Set(
      [
        ...(item.destination?.states || []),
      ].filter(isPresent)
    )
  );
  const countries = Array.from(
    new Set(
      [
        ...(item.destination?.countries || []),
      ].filter(isPresent)
    )
  );

  return { cities, states, countries };
}

function formatItemLocation(item: Deal): string | null {
  const { cities, states, countries } = getItemLocations(item);

  // Single state covering the trip: nest its cities underneath it,
  // e.g. "India (Uttarakhand: Joshimath, Govindghat, Ghangaria)".
  const locality =
    states.length === 1
      ? cities.length
        ? `${states[0]}: ${cities.join(", ")}`
        : states[0]
      : [...states, ...cities].join(", ") || null;

  if (countries.length && locality) {
    return `${countries.join(", ")} (${locality})`;
  }
  if (countries.length) {
    return countries.join(", ");
  }
  return locality;
}

// ─── Card image slideshow ─────────────────────────────────────────────────────
function CardSlideshow({
  slides,
  videoUrl,
  extraVideos,
  label,
  title,
}: {
  slides: string[];
  videoUrl?: string;
  extraVideos?: string[];
  label: string;
  title: string;
}) {
  const [idx, setIdx] = useState(0);
  const allSlides: Array<{ type: "image" | "video"; url: string }> = [
    ...slides.map((url) => ({ type: "image" as const, url })),
    ...(videoUrl ? [{ type: "video" as const, url: videoUrl }] : []),
    ...(extraVideos ?? []).map((url) => ({ type: "video" as const, url })),
  ];
  const count = allSlides.length;
  const prev = (e: React.MouseEvent) => { e.stopPropagation(); setIdx((i) => (i - 1 + count) % count); };
  const next = (e: React.MouseEvent) => { e.stopPropagation(); setIdx((i) => (i + 1) % count); };
  const current = allSlides[Math.min(idx, count - 1)];

  return (
    <div className="h-44 bg-white relative group border-b border-gray-100">
      {!current ? (
        <div className="w-full h-full flex items-center justify-center text-gray-300 font-bold text-5xl">{label[0]}</div>
      ) : current.type === "video" ? (
        <video src={current.url} className="w-full h-full object-contain" autoPlay muted loop playsInline preload="metadata" />
      ) : (
        <img
          src={current.url}
          alt={title}
          className="w-full h-full object-contain"
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
      )}
      <div className="absolute top-2 right-2 px-2 py-1 bg-white/90 backdrop-blur-sm text-[10px] font-bold rounded uppercase">
        {label}
      </div>
      {count > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
          >‹</button>
          <button
            onClick={next}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
          >›</button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {allSlides.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-white" : "bg-white/50"}`}
              />
            ))}
          </div>
          <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/40 text-white text-[10px] rounded">
            {idx + 1}/{count}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Full-screen media preview (lightbox) ────────────────────────────────────
type LightboxMedia = { type: "image" | "video" | "pdf"; url: string; name?: string };

function MediaLightbox({
  title,
  media,
  attachments,
  onClose,
  onEdit,
  editLabel = "Edit Details",
}: {
  title: string;
  media: LightboxMedia[];
  attachments?: { name: string; url: string; thumbnail_url?: string }[];
  onClose: () => void;
  onEdit?: () => void;
  editLabel?: string;
}) {
  const [idx, setIdx] = useState(0);
  const count = media.length;
  const current = count > 0 ? media[Math.min(idx, count - 1)] : null;
  // Only "pdf" media here can be a presigned S3 URL (news article
  // attachments) — gallery images/videos are public deal/webinar marketing
  // media, already served from a stable CDN, so they're left as direct
  // `src` (blob-fetching them would be pointless overhead). Loading the PDF
  // through a blob URL keeps the presigned URL out of the iframe's own
  // toolbar/address display.
  const pdfBlobUrl = useBlobUrl(current?.type === "pdf" ? current.url : null);

  useBodyScrollLock();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (count > 1 && e.key === "ArrowLeft") setIdx((i) => (i - 1 + count) % count);
      else if (count > 1 && e.key === "ArrowRight") setIdx((i) => (i + 1) % count);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, onClose]);

  const prev = () => setIdx((i) => (i - 1 + count) % count);
  const next = () => setIdx((i) => (i + 1) % count);

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex flex-col" onClick={onClose}>
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm sm:text-base font-semibold line-clamp-1">{title}</h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onEdit && (
            <button
              onClick={onEdit}
              className="px-4 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              ✏️ {editLabel}
            </button>
          )}
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-2xl leading-none"
            aria-label="Close preview"
          >×</button>
        </div>
      </div>

      {/* Stage */}
      <div className="flex-1 flex items-center justify-center relative px-4 min-h-0" onClick={(e) => e.stopPropagation()}>
        {!current ? (
          <div className="text-white/60 text-sm">No media to preview</div>
        ) : current.type === "video" ? (
          <video src={current.url} className="max-h-full max-w-full object-contain rounded-lg" controls autoPlay playsInline />
        ) : current.type === "pdf" ? (
          pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              title={current.name ?? "PDF"}
              className="w-full h-full rounded-lg bg-white"
              style={{ minHeight: "70vh" }}
            />
          ) : (
            <div className="text-white/60 text-sm">Loading…</div>
          )
        ) : (
          <img
            src={current.url}
            alt={title}
            className="max-h-full max-w-full object-contain rounded-lg"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = "0.2"; }}
          />
        )}
        {count > 1 && (
          <>
            <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white rounded-full text-2xl">‹</button>
            <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white rounded-full text-2xl">›</button>
            <div className="absolute top-3 left-1/2 -translate-x-1/2 px-2.5 py-1 bg-black/50 text-white text-xs rounded-full">{idx + 1} / {count}</div>
          </>
        )}
      </div>

      {/* Thumbnail strip + attachments */}
      <div className="px-4 py-3 space-y-3" onClick={(e) => e.stopPropagation()}>
        {count > 1 && (
          <div className="flex gap-2 overflow-x-auto justify-center">
            {media.map((m, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${i === idx ? "border-blue-500" : "border-transparent opacity-60 hover:opacity-100"}`}
              >
                {m.type === "video" ? (
                  <div className="w-full h-full bg-black flex items-center justify-center text-white text-lg">▶</div>
                ) : m.type === "pdf" ? (
                  <div className="w-full h-full bg-red-50 flex items-center justify-center text-2xl">📄</div>
                ) : (
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        )}
        {attachments && attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center">
            {attachments.map((att, i) => (
              <AttachmentLink
                key={i}
                attachment={att}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-white/10 hover:bg-white/20 rounded-lg max-w-[220px]"
                iconClassName="w-8 h-8 object-cover rounded shrink-0"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Shared renderer for `{name, url, thumbnail_url?}` attachments (deal
 * lightbox, news article view/edit). `url` is a presigned S3 URL — opened
 * via a blob object URL (see useBlobUrl) so the signed URL never appears in
 * a new tab's address bar or via "copy link"/"copy image address". */
function AttachmentLink({
  attachment,
  className,
  iconClassName,
}: {
  attachment: { name: string; url: string; thumbnail_url?: string };
  className: string;
  iconClassName: string;
}) {
  const blobUrl = useBlobUrl(attachment.url);
  return (
    <button type="button" onClick={() => void openAsBlob(attachment.url)} className={className}>
      {attachment.thumbnail_url ? (
        <img src={attachment.thumbnail_url} alt="" className={iconClassName} />
      ) : (
        <span>{attachment.name.toLowerCase().endsWith(".pdf") ? "📄" : "📝"}</span>
      )}
      <span className="truncate">{attachment.name}</span>
      {!blobUrl && <span className="text-[10px] opacity-60">…</span>}
    </button>
  );
}

function dealLightboxMedia(item: Deal): LightboxMedia[] {
  const images = previewImagesForItem(item).map((url) => ({ type: "image" as const, url }));
  const videos = galleryVideosForItem(item).map((url) => ({ type: "video" as const, url }));
  return [...images, ...videos];
}

function webinarLightboxMedia(w: WebinarCardResponse): LightboxMedia[] {
  const media: LightboxMedia[] = [];
  if (w.thumbnail_url) media.push({ type: "image", url: w.thumbnail_url });
  const vid = w.video_url || w.recording_url;
  if (vid) media.push({ type: "video", url: vid });
  return media;
}

function newsLightboxMedia(a: NewsArticle): LightboxMedia[] {
  const items: LightboxMedia[] = [];
  if (a.cover_image_url) items.push({ type: "image", url: a.cover_image_url });
  (a.gallery ?? []).forEach((g) => {
    if (g.url) items.push({ type: isVideoMediaUrl(g.url) ? "video" : "image", url: g.url });
  });
  (a.attachments ?? []).forEach((att) => {
    if (att.url && att.name.toLowerCase().endsWith(".pdf")) {
      items.push({ type: "pdf", url: att.url, name: att.name });
    }
  });
  // deduplicate by url
  const seen = new Set<string>();
  return items.filter((m) => { if (seen.has(m.url)) return false; seen.add(m.url); return true; });
}

// ─── Extended edit form ───────────────────────────────────────────────────────
interface ValidityPeriodEntry {
  dates: string[];        // individual ISO dates
  description?: string;
  original_price?: number;
  discounted_price?: number;
}

interface EditForm {
  title: string;
  description: string;
  category: string;
  original_price: string;
  discounted_price: string;
  discounted_same_as_original: boolean; // when true, discounted_price mirrors original_price on save
  currency: string;          // ISO currency code, e.g. "USD", "INR"
  dates: string[];           // individual ISO dates, e.g. ["2024-12-24","2024-12-25"]
  tags: string;              // comma-separated
  inclusions: string[];      // one row per item
  exclusions: string[];      // one row per item
  highlights: string[];      // one row per item
  min_pax: string;
  max_pax: string;
  advance_booking: string;
  cancellation_policy: string;
  blackout_dates: string;     // comma-separated ISO dates
  inventory_total: string;
  inventory_remaining: string;
  inventory_cancelled: string;
  inventory_unit: string;     // "seats" | "rooms" | "cabins" | "slots" | ""
  is_flash: boolean;
  flash_ends_at: string;      // datetime-local input value
  rail_tags: string;          // comma-separated
  cities: string;            // comma-separated
  states: string;            // comma-separated
  countries: string;         // comma-separated
  validity_periods: ValidityPeriodEntry[];
  // Category-specific details — only the fields relevant to editGroupSlug are sent on save.
  property_name: string;
  property_type: string;
  airline_name: string;
  trip_type: string;
  ship_name: string;
  cruise_line: string;
  embarkation_port: string;
  disembarkation_port: string;
  vehicle_type: string;
  vehicle_model: string;
  experience_type: string;
}

const STAY_PROPERTY_TYPES = ["hotel", "resort", "villa", "apartment", "guesthouse", "homestay", "camping", "hostel", "glamping"] as const;
const TRANSFER_VEHICLE_TYPES = ["sedan", "suv", "tempo", "bus", "luxury", "helicopter", "train"] as const;
const EXPERIENCE_TYPES = ["guided_tour", "adventure", "dining", "workshop", "show", "nightlife", "sightseeing", "concert", "safari", "theme_park", "local_experience"] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────
// ─── Row-wise add/remove list editor (inclusions, exclusions, highlights) ────
function RowListEditor({
  label,
  placeholder,
  items,
  onChange,
}: {
  label: string;
  placeholder: string;
  items: string[];
  onChange: (items: string[]) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="space-y-1.5">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input
              type="text"
              value={item}
              placeholder={placeholder}
              onChange={(e) => {
                const next = [...items];
                next[idx] = e.target.value;
                onChange(next);
              }}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="text-red-500 hover:text-red-700 text-sm font-bold px-1.5"
            >×</button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400 italic">None yet</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange([...items, ""])}
        className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
      >+ Add row</button>
    </div>
  );
}

// ─── Reusable chip editor ────────────────────────────────────────────────────
// `value` is stored as a comma-separated string (matches the existing EditForm
// shape + save logic). Renders selected entries as removable chips and offers
// suggestions (e.g. onboarding tags, geo/filter-options cities & countries).
// Free-text entries are always allowed.
function ChipsEditor({
  label,
  value,
  suggestions,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  suggestions: string[];
  placeholder?: string;
  onChange: (next: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const items = value.split(",").map((t) => t.trim()).filter(Boolean);
  const itemsLower = new Set(items.map((t) => t.toLowerCase()));

  const commit = (next: string[]) => {
    // De-dupe case-insensitively, preserve insertion order.
    const seen = new Set<string>();
    const deduped = next
      .map((t) => t.trim())
      .filter(Boolean)
      .filter((t) => {
        const k = t.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    onChange(deduped.join(", "));
  };

  const addItem = (t: string) => {
    const v = t.trim();
    if (!v) return;
    if (!itemsLower.has(v.toLowerCase())) commit([...items, v]);
    setDraft("");
  };

  const removeItem = (t: string) => commit(items.filter((x) => x !== t));

  const remainingSuggestions = suggestions
    .filter((s) => !itemsLower.has(s.toLowerCase()))
    .filter((s) => !draft.trim() || s.toLowerCase().includes(draft.trim().toLowerCase()))
    .slice(0, 12);

  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1.5">{label}</label>
      {/* Selected entries as chips */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.length === 0 && <span className="text-xs text-gray-400 italic">None yet</span>}
        {items.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
            {t}
            <button
              type="button"
              onClick={() => removeItem(t)}
              className="text-blue-500 hover:text-blue-800 font-bold leading-none"
              aria-label={`Remove ${t}`}
            >×</button>
          </span>
        ))}
      </div>
      {/* Free-text input — Enter or comma adds an entry */}
      <input
        type="text"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder={placeholder || "Type and press Enter"}
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          if (v.includes(",")) {
            const parts = v.split(",");
            const last = parts.pop() ?? "";
            commit([...items, ...parts]);
            setDraft(last);
          } else {
            setDraft(v);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); addItem(draft); }
          else if (e.key === "Backspace" && !draft && items.length) { removeItem(items[items.length - 1]); }
        }}
      />
      {/* Suggestions */}
      {remainingSuggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {remainingSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addItem(s)}
              className="inline-flex items-center px-2.5 py-1 bg-gray-100 hover:bg-blue-50 text-gray-600 hover:text-blue-700 text-xs font-medium rounded-full border border-gray-200"
            >+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

interface CurrencyOption { code: string; symbol: string; label: string }

// Built-in fallback — used when the onboarding "currencies" option set is empty
// or unreachable. Admins curate the live list via PUT /api/v1/onboarding/options/currencies.
const CURRENCY_OPTIONS: CurrencyOption[] = [
  { code: "INR", symbol: "₹", label: "Indian Rupee" },
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "Pound Sterling" },
  { code: "AED", symbol: "د.إ", label: "UAE Dirham" },
  { code: "SGD", symbol: "S$", label: "Singapore Dollar" },
];

// Best-effort symbols for currencies an admin may add beyond the built-in set.
const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", SGD: "S$",
  JPY: "¥", CNY: "¥", AUD: "A$", CAD: "C$", CHF: "CHF", THB: "฿",
  MYR: "RM", IDR: "Rp", SAR: "﷼", QAR: "﷼", ZAR: "R", NZD: "NZ$", HKD: "HK$",
};

function currencySymbol(currency?: string): string {
  return CURRENCY_SYMBOLS[(currency || "").toUpperCase()] || (currency || "").toUpperCase() || "₹";
}

/** Session-scoped dismissal for the optional price nudge below. Purely
 *  cosmetic — nothing about the deal depends on it — so a sessionStorage flag
 *  (gone when the tab closes) is enough and needs no server round-trip. */
const priceNudgeDismissKey = (id: string) => `gv:price-nudge-dismissed:${id}`;

/** Optional, dismissible invitation to add a price — rendered only once this
 *  deal's AI pipeline job reports "completed" and nothing in `pricing` yields a
 *  number (see hasStructuredPrice). Never required, never gates publish/save:
 *  it writes only `pricing` through the same PUT the edit modal uses. */
function PriceNudge({ item, onSaved }: { item: Deal; onSaved: () => void }) {
  const id = dealId(item);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return Boolean(id) && sessionStorage.getItem(priceNudgeDismissKey(id)) === "1";
    } catch {
      return false;
    }
  });
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(
    ((item.pricing as { currency?: string } | undefined)?.currency || "INR").toUpperCase(),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (dismissed || !id || !dealCollection(item)) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(priceNudgeDismissKey(id), "1");
    } catch {
      /* private mode / storage disabled — component state still hides it */
    }
    setDismissed(true);
  };

  const save = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter an amount greater than 0.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await dealsService.updateInventoryItem(dealCollection(item), id, {
        pricing: { original_price: value, currency: currency.trim() || undefined },
      });
      setDismissed(true);
      onSaved();
    } catch (err: unknown) {
      setError(describeDealError(err, "Couldn't save the price. You can add it from Edit instead."));
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-blue-800">
          Add a price for this deal <span className="font-normal text-blue-600">(optional)</span> — deals with pricing get more views.
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss price suggestion"
          className="shrink-0 text-blue-400 hover:text-blue-700"
        >
          ✕
        </button>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <input
          value={currency}
          onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
          aria-label="Currency"
          className="w-16 rounded-lg border border-blue-200 px-2 py-1 text-xs uppercase"
        />
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder="Price"
          aria-label="Price"
          className="w-24 rounded-lg border border-blue-200 px-2 py-1 text-xs"
        />
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg px-2 py-1 text-xs font-medium text-blue-500 hover:text-blue-800"
        >
          Skip
        </button>
      </div>
      {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
    </div>
  );
}

// Coerce onboarding "currencies" items (strings like "USD" or objects
// {code,symbol,label}) into CurrencyOption[]. Returns null when nothing usable.
function coerceCurrencyOptions(items: unknown): CurrencyOption[] | null {
  if (!Array.isArray(items) || items.length === 0) return null;
  const seen = new Set<string>();
  const out: CurrencyOption[] = [];
  const pushUnique = (opt: CurrencyOption) => {
    if (seen.has(opt.code)) return;
    seen.add(opt.code);
    out.push(opt);
  };
  for (const it of items) {
    if (typeof it === "string") {
      const code = it.trim().toUpperCase();
      if (code) pushUnique({ code, symbol: currencySymbol(code), label: code });
    } else if (it && typeof it === "object") {
      const o = it as Record<string, unknown>;
      // Admin-curated entries store `value` as the currency *symbol* (e.g. "$", "₹")
      // and `label` as "USD ($) - US Dollar" — the ISO code only lives in `code`
      // (if present) or the label's leading token. Mirrors the backend's own
      // fallback parsing (deals/typed/validator.py get_allowed_currencies) so the
      // dropdown never offers a raw symbol as a selectable "code".
      const isAlpha3 = (s: string) => /^[A-Z]{3}$/.test(s);
      const explicit = String(o.code ?? "").trim().toUpperCase();
      const rawValue = String(o.value ?? "").trim().toUpperCase();
      const label = String(o.label ?? o.name ?? "").trim();
      let code = "";
      if (isAlpha3(explicit)) code = explicit;
      else if (isAlpha3(rawValue)) code = rawValue;
      else {
        const leading = label.split(" ", 1)[0]?.trim().toUpperCase() ?? "";
        if (isAlpha3(leading)) code = leading;
      }
      if (code) pushUnique({
        code,
        symbol: String(o.symbol ?? o.value ?? currencySymbol(code)),
        label: label || code,
      });
    }
  }
  return out.length ? out : null;
}

function discountPct(orig: string, disc: string): number | null {
  const o = parseFloat(orig);
  const d = parseFloat(disc);
  if (!o || !d || d >= o) return null;
  return Math.round(((o - d) / o) * 100);
}

function formatBuyerLabel(event: SellerAnalyticsEvent): string {
  return (
    event.buyer?.name ||
    event.buyer?.email ||
    event.buyer?.phone ||
    "Anonymous visitor"
  );
}

function formatEventLabel(event: SellerAnalyticsEvent): string {
  if (event.event_type === "contact_click" && event.channel === "whatsapp") {
    return "WhatsApp click";
  }
  if (event.event_type === "contact_click" && event.channel === "email") {
    return "Email click";
  }
  if (event.event_type === "save_for_later") {
    return "Saved for later";
  }
  if (event.event_type === "unsave_for_later") {
    return "Removed from saved";
  }
  return event.event_type;
}

// ─── Seller Score Widget ─────────────────────────────────────────────────────

function SellerScoreWidget({
  score,
  status,
}: {
  score: SellerScore | null;
  status: SellerPreferenceStatus | null;
}) {
  if (!score && !status) return null;

  const validationColor = status?.inventory_validated
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status?.pending_nudge
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-gray-50 text-gray-600 border-gray-200";

  const validationLabel = status?.inventory_validated
    ? "Preferences validated"
    : status?.pending_nudge
    ? "Upload deals to validate preferences"
    : status?.preferences
    ? "Preferences set — awaiting inventory"
    : "No preferences set";

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
      <div className="flex flex-wrap items-start gap-4">
        {/* Inventory Score */}
        {score && (
          <div className="flex-1 min-w-[120px]">
            <p className="text-xs text-gray-500 mb-0.5">Inventory Score</p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-gray-900">
                {Math.round(score.inventory_score)}
              </span>
              <span className="text-xs text-gray-400">pts</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {score.active_deal_count} active deal{score.active_deal_count !== 1 ? "s" : ""}
            </p>
          </div>
        )}

        {/* Coverage */}
        {score && (score.countries?.length > 0 || score.tags?.length > 0) && (
          <div className="flex-1 min-w-[160px]">
            <p className="text-xs text-gray-500 mb-1.5">Your Coverage</p>
            <div className="flex flex-wrap gap-1">
              {score.countries?.slice(0, 3).map((c) => (
                <span key={c} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-100">
                  {c}
                </span>
              ))}
              {(score.countries?.length ?? 0) > 3 && (
                <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                  +{score.countries.length - 3} more
                </span>
              )}
              {score.tags?.slice(0, 2).map((t) => (
                <span key={t} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-xs rounded border border-purple-100">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Validation Status */}
        {status && (
          <div className="flex-shrink-0">
            <p className="text-xs text-gray-500 mb-1.5">Preference Status</p>
            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${validationColor}`}>
              {validationLabel}
            </span>
            {status.pending_nudge && !status.inventory_validated && (
              <p className="text-xs text-amber-600 mt-1 max-w-[200px]">
                Upload deals matching your preferences to appear in buyer recommendations.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────
const MyInventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuth();
  const { jobs: bgJobs } = useBackgroundJobs();
  // ISO2 -> full name, for resolving pending_countries codes in the
  // "market pending review" modal below (falls back to the raw code).
  const { countries: geoCountries } = useCountries();
  const countryNameByIso2 = new Map(geoCountries.map((c) => [c.iso2, c.name]));
  const [inventory, setInventory] = useState<Deal[]>([]);
  const [inventorySkip, setInventorySkip] = useState(0);
  const [inventoryHasMore, setInventoryHasMore] = useState(false);
  const [inventoryLoadingMore, setInventoryLoadingMore] = useState(false);
  const INVENTORY_PAGE_SIZE = 20;
  const [activeSection, setActiveSection] = useState<"deals" | "webinars" | "contacts" | "news">("deals");
  // Tracks which tabs/panels have already had their data fetched, so switching
  // tabs doesn't re-fetch on every click and unopened tabs never fetch at all.
  const [loadedSections, setLoadedSections] = useState<Set<string>>(new Set(["deals"]));
  const [webinars, setWebinars] = useState<WebinarCardResponse[]>([]);
  const [contacts, setContacts] = useState<BusinessContactResponse[]>([]);
  const [myNews, setMyNews] = useState<NewsArticle[]>([]);
  // Full-screen media previews
  const [previewItem, setPreviewItem] = useState<Deal | null>(null);
  const [previewWebinar, setPreviewWebinar] = useState<WebinarCardResponse | null>(null);
  const [previewNews, setPreviewNews] = useState<NewsArticle | null>(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsArticle | null>(null);
  const [newsSaving, setNewsSaving] = useState(false);
  const [newsEditError, setNewsEditError] = useState("");
  const [newsDeleteId, setNewsDeleteId] = useState<string | null>(null);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(() => localStorage.getItem("inv_show_analytics") !== "false");
  const [sellerAnalytics, setSellerAnalytics] = useState<SellerAnalyticsResponse | null>(null);
  const [sellerDashboard, setSellerDashboard] = useState<SellerDashboardResponse | null>(null);
  const [dashboardDays, setDashboardDays] = useState(30);
  const [chartActiveSeries, setChartActiveSeries] = useState<Set<string>>(new Set(["deal_views", "enquiries", "whatsapp_clicks", "deal_likes"]));
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  // Deep-link target from a notification (?deal_id=... from notificationDeepLink.ts) —
  // highlight + scroll to the specific deal card once inventory has loaded.
  const notifiedDealId = new URLSearchParams(location.search).get("deal_id");
  const [highlightedDealId, setHighlightedDealId] = useState<string | null>(notifiedDealId);

  useEffect(() => {
    if (!highlightedDealId || inventory.length === 0) return;
    const el = document.getElementById(`inventory-item-${highlightedDealId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Clear the highlight ring after a few seconds so it doesn't linger forever.
    const timer = window.setTimeout(() => setHighlightedDealId(null), 4000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedDealId, inventory.length]);

  // Seller recommendation signals
  const [sellerScore, setSellerScore] = useState<SellerScore | null>(null);
  const [sellerPrefStatus, setSellerPrefStatus] = useState<SellerPreferenceStatus | null>(null);

  // Edit modal state
  const [editingItem, setEditingItem] = useState<Deal | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    title: "",
    description: "",
    category: "",
    original_price: "",
    discounted_price: "",
    discounted_same_as_original: false,
    currency: "INR",
    dates: [],
    tags: "",
    inclusions: [],
    exclusions: [],
    highlights: [],
    min_pax: "",
    max_pax: "",
    advance_booking: "",
    cancellation_policy: "",
    blackout_dates: "",
    inventory_total: "",
    inventory_remaining: "",
    inventory_cancelled: "",
    inventory_unit: "",
    is_flash: false,
    flash_ends_at: "",
    rail_tags: "",
    cities: "",
    states: "",
    countries: "",
    validity_periods: [],
    property_name: "",
    property_type: "",
    airline_name: "",
    trip_type: "",
    ship_name: "",
    cruise_line: "",
    embarkation_port: "",
    disembarkation_port: "",
    vehicle_type: "",
    vehicle_model: "",
    experience_type: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteDeal, setConfirmDeleteDeal] = useState<Deal | null>(null);
  const [pendingGalleryRemovals, setPendingGalleryRemovals] = useState<string[]>([]);
  const [showGalleryDeleteConfirm, setShowGalleryDeleteConfirm] = useState(false);
  const [isApplyingAiField, setIsApplyingAiField] = useState<"title" | "description" | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadMediaError, setUploadMediaError] = useState<string | null>(null);
  // Gallery files selected but not yet uploaded — they are sent to the gallery
  // route only when "Save changes" is clicked (deferred upload), not on select.
  const [pendingGalleryFiles, setPendingGalleryFiles] = useState<
    { file: File; previewUrl: string; isVideo: boolean }[]
  >([]);

  // ── Slug / deal-type / package edit state ─────────────────────────────────
  const [editPrimarySlug, setEditPrimarySlug] = useState("");
  const [editGroupSlug, setEditGroupSlug] = useState("");   // deal type / typed collection
  const [editSecondarySlug, setEditSecondarySlugs] = useState<string[]>([]);
  const [editIsInPackages, setEditIsInPackages] = useState(false);
  const [editPkgPrimarySlug, setEditPkgPrimarySlug] = useState("");
  const [editPkgSecondarySlugs, setEditPkgSecondarySlugs] = useState<string[]>([]);
  type EditModalTab = "basic" | "pricing" | "category" | "media" | "packaging";
  const [editModalTab, setEditModalTab] = useState<EditModalTab>("basic");
  // `pricing_required` is optional: the value arrives from an unchecked `as` cast of
  // the /api/v2/categories response, and every read below treats undefined as
  // "pricing required" (fail-safe), so the type must admit undefined too.
  const [categoryOptions, setCategoryOptions] = useState<{ slug: string; name: string; group_slug: string; is_promotional: boolean; pricing_required?: boolean }[]>([]);
  const [categoryLoadError, setCategoryLoadError] = useState<string | null>(null);
  // Available tag suggestions, sourced from the onboarding option set ("tags").
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  // Destination suggestions (chips), sourced from typed-deals filter-options.
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  // Currency options — admin-curatable via onboarding "currencies"; falls back
  // to the built-in CURRENCY_OPTIONS when the option set is absent.
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyOption[]>(CURRENCY_OPTIONS);

  // Single source of truth for "the deal being edited doesn't take pricing" —
  // consumed by both the edit modal's Pricing section and the save payload builder,
  // so the UI and the payload can never disagree.
  //
  // Media-group deals (webinars/ai_contact/ai_showcase/flyer_video) are typed as
  // DealMedia server-side, which requires pricing to be absent entirely. Second,
  // independent check (additive to that media-group check): an admin-flagged
  // pricing_required === false category also skips pricing. Missing/undefined
  // pricing_required defaults to "required" (fail-safe).
  //
  // Resolved from the in-form category selection FIRST (the seller may be changing
  // category and pricing in the same save, in which case the new category's rule is
  // the one that applies), falling back to the deal's stored primary slug. Never
  // secondary/subcategory slugs.
  const editPrimarySlugForPricing = editForm.category || editingItem?.categories?.primary?.slug;
  const editGroupForPricing =
    ((editingItem?.categories as Record<string, unknown> | undefined)?.group_slug as string | undefined) || editGroupSlug;
  const editPricingNotRequired =
    categoryOptions.find((c) => c.slug === editPrimarySlugForPricing)?.pricing_required === false;
  const editPricingExempt = editGroupForPricing === "media" || editPricingNotRequired;

  useEffect(() => {
    fetch(`${env.apiBaseUrl}/api/v2/categories`, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`Categories API error ${r.status}`);
        return r.json();
      })
      .then((data: unknown) => {
        if (Array.isArray(data)) {
          if (data.length === 0) setCategoryLoadError("No categories configured in this environment.");
          else setCategoryOptions(data as typeof categoryOptions);
        } else {
          setCategoryLoadError("Unexpected response from categories API.");
        }
      })
      .catch((e: unknown) => setCategoryLoadError(e instanceof Error ? e.message : "Failed to load categories."));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Onboarding tag options — used as chip suggestions in the deal editor.
    fetch(`${env.apiBaseUrl}/api/v1/onboarding/options/tags`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: unknown) => {
        const items = (data as { items?: unknown } | null)?.items;
        if (Array.isArray(items)) {
          setAvailableTags(items.map((t) => String(t).trim()).filter(Boolean));
        }
      })
      .catch(() => { /* suggestions are optional — free-text still works */ });
  }, []);

  useEffect(() => {
    // Destination suggestions — distinct cities & countries across live deals.
    fetch(`${env.apiBaseUrl}/api/typed-deals/filter-options`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: unknown) => {
        const d = (data as { cities?: unknown; states?: unknown; countries?: unknown } | null) || {};
        if (Array.isArray(d.cities)) setAvailableCities(d.cities.map((c) => String(c).trim()).filter(Boolean));
        if (Array.isArray(d.states)) setAvailableStates(d.states.map((s) => String(s).trim()).filter(Boolean));
        if (Array.isArray(d.countries)) setAvailableCountries(d.countries.map((c) => String(c).trim()).filter(Boolean));
      })
      .catch(() => { /* suggestions are optional */ });
  }, []);

  useEffect(() => {
    // Admin-curated currency list (onboarding "currencies"); keeps the built-in
    // fallback when the option set is absent or empty.
    fetch(`${env.apiBaseUrl}/api/v1/onboarding/options/currencies`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: unknown) => {
        const coerced = coerceCurrencyOptions((data as { items?: unknown } | null)?.items);
        if (coerced) setCurrencyOptions(coerced);
      })
      .catch(() => { /* keep built-in CURRENCY_OPTIONS */ });
  }, []);

  const DEAL_TYPE_OPTIONS_INV = [
    { value: "stays",       label: "Stays" },
    { value: "experiences", label: "Experiences" },
    { value: "services",    label: "Services" },
    { value: "flights",     label: "Flights" },
    { value: "transfers",   label: "Transfers" },
    { value: "cruises",     label: "Cruises" },
    { value: "packages",    label: "Packages" },
  ] as const;

  // ── Data fetch ──────────────────────────────────────────────────────────────
  const fetchSellerRecommendationData = async () => {
    if (!user?.id) return;
    try {
      const ctx = await recommendationsService.getMyRecommendationContext();
      if (ctx.role === "seller") {
        setSellerScore(ctx.score);
        setSellerPrefStatus(ctx.preference_status);
      }
    } catch {
      // non-critical — swallow silently
    }
  };

  const fetchMyInventory = async (skip = 0, append = false) => {
    if (append) setInventoryLoadingMore(true);
    else setIsLoading(true);
    setError(null);
    try {
      const data = await dealsService.listMyInventory(skip, INVENTORY_PAGE_SIZE + 1);
      const hasMore = data.length > INVENTORY_PAGE_SIZE;
      const page = data.slice(0, INVENTORY_PAGE_SIZE);
      if (append) {
        setInventory(prev => {
          const existing = new Set(prev.map(d => d.id ?? d.dealer_id));
          return [...prev, ...page.filter(d => !existing.has(d.id ?? d.dealer_id))];
        });
      } else {
        setInventory(page);
      }
      setInventorySkip(skip + page.length);
      setInventoryHasMore(hasMore);
    } catch (err: unknown) {
      console.error("Failed to fetch my inventory:", err);
      setError("Unable to load your inventory.");
    } finally {
      setIsLoading(false);
      setInventoryLoadingMore(false);
    }
  };

  const fetchMyWebinars = async () => {
    try {
      const data = await webinarsService.listMyWebinars();
      const myOnly = (data.items || []).map((webinar) => ({
        ...webinar,
        is_own_webinar: true,
        owner_label: "own webinar",
      }));
      setWebinars(myOnly);
    } catch (err: unknown) {
      console.error("Failed to fetch webinars:", err);
    }
  };

  const fetchMyContacts = async () => {
    try {
      const data = await contactsService.listMyContacts();
      setContacts(data.items || []);
    } catch (err: unknown) {
      console.error("Failed to fetch contacts:", err);
    }
  };

  const fetchMyNews = async () => {
    setNewsLoading(true);
    try {
      const data = await newsService.listMyNews();
      setMyNews(data.items || []);
    } catch (err: unknown) {
      console.error("Failed to fetch my news:", err);
    } finally {
      setNewsLoading(false);
    }
  };

  const handleSaveNews = async (articleId: string, fd: FormData) => {
    setNewsSaving(true);
    setNewsEditError("");
    try {
      const updated = await newsService.updateMyNews(articleId, fd);
      setMyNews((prev) => prev.map((a) => a.id === articleId ? { ...a, ...updated } : a));
      setEditingNews(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setNewsEditError(typeof msg === "string" ? msg : "Failed to save. Please try again.");
    } finally {
      setNewsSaving(false);
    }
  };

  const handleDeleteNews = async (articleId: string) => {
    try {
      await newsService.deleteMyNews(articleId);
      setMyNews((prev) => prev.filter((a) => a.id !== articleId));
      setNewsDeleteId(null);
    } catch {
      // silently ignore — refresh will reconcile
      await fetchMyNews();
    }
  };

  const ANALYTICS_CACHE_KEY = "seller_analytics_cache";
  const DASHBOARD_CACHE_KEY = "seller_dashboard_cache";

  const fetchSellerAnalytics = async (days = dashboardDays) => {
    // Restore from cache immediately so analytics appear before fetch completes
    try {
      const cachedAnalytics = localStorage.getItem(ANALYTICS_CACHE_KEY);
      const cachedDashboard = localStorage.getItem(`${DASHBOARD_CACHE_KEY}_${days}`);
      if (cachedAnalytics) setSellerAnalytics(JSON.parse(cachedAnalytics) as SellerAnalyticsResponse);
      if (cachedDashboard) setSellerDashboard(JSON.parse(cachedDashboard) as SellerDashboardResponse);
    } catch {
      // ignore stale/corrupt cache
    }

    setIsAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const [legacy, dashboard] = await Promise.allSettled([
        analyticsService.getSellerAnalytics(),
        analyticsService.getSellerDashboard(days),
      ]);
      if (legacy.status === "fulfilled") {
        setSellerAnalytics(legacy.value);
        try { localStorage.setItem(ANALYTICS_CACHE_KEY, JSON.stringify(legacy.value)); } catch { /* quota exceeded */ }
      }
      if (dashboard.status === "fulfilled") {
        setSellerDashboard(dashboard.value);
        try { localStorage.setItem(`${DASHBOARD_CACHE_KEY}_${days}`, JSON.stringify(dashboard.value)); } catch { /* quota exceeded */ }
      }
      if (legacy.status === "rejected" && dashboard.status === "rejected") {
        setAnalyticsError("Unable to load analytics right now.");
      }
    } catch (err: unknown) {
      console.error("Failed to fetch seller analytics:", err);
      setAnalyticsError("Unable to load analytics right now.");
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      // Always needed: deals tab is the default view, and the recommendation
      // context drives the always-visible SellerScoreWidget. Analytics is
      // handled by the lazy-load effect below (it also covers the initial
      // mount when the panel starts expanded).
      void fetchMyInventory();
      void fetchSellerRecommendationData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Lazy-load each tab's data the first time it's opened, and lazy-load
  // analytics the first time the panel is expanded.
  useEffect(() => {
    if (!isAuthenticated || loadedSections.has(activeSection)) return;
    setLoadedSections((prev) => new Set(prev).add(activeSection));
    if (activeSection === "webinars") void fetchMyWebinars();
    else if (activeSection === "contacts") void fetchMyContacts();
    else if (activeSection === "news") void fetchMyNews();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !showAnalytics || loadedSections.has("analytics")) return;
    setLoadedSections((prev) => new Set(prev).add("analytics"));
    void fetchSellerAnalytics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnalytics, isAuthenticated]);

  // Auto-reload inventory when AI analysis completes or deal is received
  useEffect(() => {
    const handler = () => { void fetchMyInventory(); };
    window.addEventListener("inventory:deal_updated", handler);
    return () => window.removeEventListener("inventory:deal_updated", handler);
  }, []);

  useEffect(() => {
    const routeState = location.state as { editItemId?: string } | null;
    const editItemId = routeState?.editItemId;
    if (!editItemId || inventory.length === 0 || editingItem) return;

    const createdItem = inventory.find((item) => item.id === editItemId);
    if (!createdItem) return;

    openEditModal(createdItem);
    navigate("/inventory/my", { replace: true, state: null });
  }, [inventory, editingItem, location.state, navigate]);

  // ── Like ────────────────────────────────────────────────────────────────────
  const handleLike = async (id: string, collection: string) => {
    try {
      await dealsService.likeDeal(id, collection);
      await fetchMyInventory();
    } catch (err: unknown) {
      console.error("Failed to like deal:", err);
    }
  };

  // ── Edit modal open/close ───────────────────────────────────────────────────
  const openEditModal = (item: Deal) => {
    const raw = item as unknown as Record<string, unknown>;
    const validity = (raw.validity as Record<string, unknown>) || {};
    const aiExtracted = (raw.ai_extracted_raw as Record<string, unknown>) ?? {};
    const aiCategoryDetails = (aiExtracted.category_details as Record<string, unknown>) ?? {};

    // Group resolution: prefer categories.group_slug, then fall back to the
    // storage collection name (deals may predate the typed categories block).
    const COLLECTION_TO_GROUP: Record<string, string> = {
      deal_stays: "stays", deal_packages: "packages", deal_flights: "flights",
      deal_cruises: "cruises", deal_transfers: "transfers", deal_experiences: "experiences",
      deal_services: "services", deal_media: "media",
    };
    const group: string =
      ((item.categories as Record<string, unknown> | undefined)?.group_slug as string) ||
      COLLECTION_TO_GROUP[dealCollection(item)] || "";

    // inclusions/exclusions/highlights/tags/pax live on different places
    // depending on when the deal was created: dedicated typed nested block
    // (experience/package), `extras`, or — most commonly for AI-analysed
    // deals — only inside `ai_extracted_raw` (never copied into the typed
    // doc's category block). Check all three, typed block first.
    const nestedListSource: Record<string, unknown> =
      group === "experiences" ? ((raw.experience as Record<string, unknown>) ?? {})
      : group === "packages" ? ((raw.package as Record<string, unknown>) ?? {})
      : {};
    const extrasList = Array.isArray(raw.extras) ? (raw.extras as { name: string; value: unknown }[]) : [];
    const extraValue = (name: string) => extrasList.find((e) => e.name === name)?.value;
    const resolveList = (key: string): string[] => {
      const nested = nestedListSource[key];
      if (Array.isArray(nested) && nested.length > 0) return nested as string[];
      const fromExtras = extraValue(key);
      if (typeof fromExtras === "string" && fromExtras) return fromExtras.split(";").map((s) => s.trim()).filter(Boolean);
      const fromAi = aiExtracted[key];
      if (Array.isArray(fromAi)) return fromAi as string[];
      return [];
    };
    // `terms.min_pax`/`terms.max_pax` is the structured home going forward; fall
    // back to the legacy extras/ai_extracted_raw locations for deals saved before
    // this field existed so they don't show blank.
    const termsBlock = (raw.terms as Record<string, unknown> | undefined) ?? {};
    const minPaxVal = termsBlock.min_pax ?? (group === "experiences"
      ? (nestedListSource.group_size as Record<string, unknown> | undefined)?.min ?? extraValue("min_pax") ?? aiExtracted.min_pax
      : extraValue("min_pax") ?? aiExtracted.min_pax);
    const maxPaxVal = termsBlock.max_pax ?? (group === "experiences"
      ? (nestedListSource.group_size as Record<string, unknown> | undefined)?.max ?? extraValue("max_pax") ?? aiExtracted.max_pax
      : extraValue("max_pax") ?? aiExtracted.max_pax);
    const resolvedDates = Array.isArray(validity.dates) && (validity.dates as string[]).length > 0
      ? (validity.dates as string[])
      : Array.isArray(aiExtracted.dates) ? (aiExtracted.dates as string[]) : [];
    const resolvedTags = Array.isArray(item.tags) && item.tags.length > 0
      ? (item.tags as string[])
      : Array.isArray(aiExtracted.tags) ? (aiExtracted.tags as string[]) : [];

    // Category-specific details: prefer the typed nested block; fall back to
    // ai_extracted_raw.category_details when the typed doc was never backfilled.
    const stayBlock = (raw.stay as Record<string, unknown>) ?? {};
    const flightBlock = (raw.flight as Record<string, unknown>) ?? {};
    const flightAirline = (flightBlock.airline as Record<string, unknown>) ?? {};
    const cruiseBlock = (raw.cruise as Record<string, unknown>) ?? {};
    const cruiseShip = (cruiseBlock.ship as Record<string, unknown>) ?? {};
    const transferBlock = (raw.transfer as Record<string, unknown>) ?? {};
    const transferVehicle = (transferBlock.vehicle as Record<string, unknown>) ?? {};
    const experienceBlock = (raw.experience as Record<string, unknown>) ?? {};
    const pick = (typedVal: unknown, aiKey: string) =>
      String(typedVal || aiCategoryDetails[aiKey] || "");

    setEditingItem(item);
    // Second, independent check (additive to the media-group check below):
    // an admin can flag a non-media category as not requiring pricing via
    // Settings. Missing/undefined pricing_required must default to "required"
    // (fail-safe), so only an explicit `=== false` hides pricing here.
    const primarySlugForPricing = item.categories?.primary?.slug;
    const primaryCategoryForPricing = categoryOptions.find((c) => c.slug === primarySlugForPricing);
    const pricingNotRequiredForPrefill = primaryCategoryForPricing?.pricing_required === false;
    setEditForm({
      title: item.title || "",
      description: item.description || "",
      category: item.categories?.primary?.slug || "",
      // Media-group deals (webinars/ai_contact/ai_showcase/flyer_video) never have
      // pricing — leave blank rather than defaulting to "0", which would otherwise
      // get sent back on save and trip the backend's price-must-be->0 validation.
      original_price: (group === "media" || pricingNotRequiredForPrefill) ? "" : (((item.pricing as { original_price?: number } | undefined)?.original_price ?? 0)?.toString() || ""),
      discounted_price: (group === "media" || pricingNotRequiredForPrefill) ? "" : (((item.pricing as { discounted_price?: number } | undefined)?.discounted_price ?? 0)?.toString() || ""),
      // Never pre-checked, even when the stored discounted_price already equals original —
      // the seller must always be able to open Edit and set/change a discount independently.
      discounted_same_as_original: false,
      currency: ((item.pricing as { currency?: string } | undefined)?.currency || "INR").toUpperCase(),
      dates: resolvedDates,
      tags: resolvedTags.join(", "),
      inclusions: resolveList("inclusions"),
      exclusions: resolveList("exclusions"),
      highlights: resolveList("highlights"),
      min_pax: String(minPaxVal ?? ""),
      max_pax: String(maxPaxVal ?? ""),
      advance_booking: String(termsBlock.advance_booking ?? ""),
      cancellation_policy: String(termsBlock.cancellation_policy ?? ""),
      blackout_dates: Array.isArray(termsBlock.blackout_dates) ? (termsBlock.blackout_dates as string[]).join(", ") : "",
      inventory_total: String((raw.inventory as Record<string, unknown> | undefined)?.total ?? ""),
      inventory_remaining: String((raw.inventory as Record<string, unknown> | undefined)?.remaining ?? ""),
      inventory_cancelled: String((raw.inventory as Record<string, unknown> | undefined)?.cancelled ?? ""),
      inventory_unit: String((raw.inventory as Record<string, unknown> | undefined)?.unit ?? ""),
      is_flash: Boolean(raw.is_flash),
      flash_ends_at: raw.flash_ends_at ? String(raw.flash_ends_at).slice(0, 16) : "",
      rail_tags: Array.isArray(raw.rail_tags) ? (raw.rail_tags as string[]).join(", ") : "",
      cities: (() => {
        const dest = (raw.destination as Record<string,unknown> | undefined);
        return Array.isArray(dest?.cities) ? (dest!.cities as string[]).join(", ") : "";
      })(),
      states: (() => {
        const dest = (raw.destination as Record<string,unknown> | undefined);
        return Array.isArray(dest?.states) ? (dest!.states as string[]).join(", ") : "";
      })(),
      countries: (() => {
        const dest = (raw.destination as Record<string,unknown> | undefined);
        return Array.isArray(dest?.countries) ? (dest!.countries as string[]).join(", ") : "";
      })(),
      validity_periods: Array.isArray(validity.validity_periods) && (validity.validity_periods as unknown[]).length > 0
        ? (validity.validity_periods as ValidityPeriodEntry[])
        : Array.isArray(aiExtracted.validity_periods) ? (aiExtracted.validity_periods as ValidityPeriodEntry[]) : [],
      property_name: pick(stayBlock.property_name, "property_name"),
      property_type: pick(stayBlock.property_type, "property_type"),
      airline_name: pick(flightAirline.name, "airline_name"),
      trip_type: pick(flightBlock.trip_type, "trip_type"),
      ship_name: pick(cruiseShip.name, "ship_name"),
      cruise_line: pick(cruiseShip.cruise_line, "cruise_line"),
      embarkation_port: pick(cruiseBlock.embarkation_port, "embarkation_port"),
      disembarkation_port: pick(cruiseBlock.disembarkation_port, "disembarkation_port"),
      vehicle_type: pick(transferVehicle.vehicle_type, "vehicle_type"),
      vehicle_model: pick(transferVehicle.vehicle_model, "vehicle_model"),
      experience_type: pick(experienceBlock.experience_type, "experience_type"),
    });
    const cats = item.categories as Record<string, unknown> | undefined;
    setEditPrimarySlug((cats?.primary_slug as string) || "");
    setEditGroupSlug((cats?.group_slug as string) || group);
    setEditSecondarySlugs((cats?.secondary_slugs as string[]) || []);
    setEditIsInPackages(!!(item as Record<string, unknown>).is_in_packages);
    setEditPkgPrimarySlug((cats?.package_primary_slug as string) || "");
    setEditPkgSecondarySlugs((cats?.package_secondary_slugs as string[]) || []);
    setEditModalTab("basic");
    setPendingGalleryRemovals([]);
    setShowGalleryDeleteConfirm(false);
    setPendingGalleryFiles((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setUploadMediaError(null);
    setSaveError(null);
  };

  const closeEditModal = () => {
    setEditingItem(null);
    setSaveError(null);
    setIsDeleting(false);
    setIsApplyingAiField(null);
    setPendingGalleryFiles((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setUploadMediaError(null);
  };

  const mergeEditedItem = (nextItem: Deal) => {
    setEditingItem(nextItem);
    setInventory((current) =>
      current.map((item) => (item.id === nextItem.id ? nextItem : item))
    );
  };

  // ── Upload extra media ───────────────────────────────────────────────────────
  const markGalleryForRemoval = (resolvedUrl: string) => {
    setPendingGalleryRemovals((prev) => prev.includes(resolvedUrl) ? prev : [...prev, resolvedUrl]);
  };

  // Queue gallery files locally — they are NOT uploaded here. The actual
  // gallery-route upload happens in handleSave (after which the PUT runs).
  const handleQueueExtraMedia = (files: FileList | File[]) => {
    if (!editingItem) return;
    const fileArr = Array.from(files).filter((file) => GALLERY_ACCEPTED_TYPES.includes(file.type));
    if (!fileArr.length) return;
    setUploadMediaError(null);
    const queued = fileArr.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      isVideo: file.type.startsWith("video/"),
    }));
    setPendingGalleryFiles((prev) => [...prev, ...queued]);
  };

  const removePendingGalleryFile = (previewUrl: string) => {
    setPendingGalleryFiles((prev) => {
      const target = prev.find((p) => p.previewUrl === previewUrl);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.previewUrl !== previewUrl);
    });
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editingItem) return;
    // If gallery removals are pending, show confirm before proceeding
    if (pendingGalleryRemovals.length > 0 && !showGalleryDeleteConfirm) {
      setShowGalleryDeleteConfirm(true);
      return;
    }
    setShowGalleryDeleteConfirm(false);
    setIsSaving(true);
    setSaveError(null);
    setUploadMediaError(null);
    try {
      // 1) Upload any queued gallery files FIRST via the gallery route. Only
      //    after these return 200 do we proceed to the PUT inventory update.
      //    The gallery route does not run AI analysis, so the primary
      //    (clean) image stays as-is — nothing is re-analysed.
      let uploadedGalleryKeys: string[] = [];
      if (pendingGalleryFiles.length > 0) {
        setUploadingMedia(true);
        const results = await dealsService.uploadGalleryFiles(
          dealId(editingItem),
          pendingGalleryFiles.map((p) => p.file),
        );
        uploadedGalleryKeys = results.map((r) => r.key).filter(Boolean);
        setUploadingMedia(false);
      }

      const payload: Record<string, unknown> = {};
      if (editForm.title) payload.title = editForm.title;
      if (editForm.description) payload.description = editForm.description;

      const cat = editingItem.categories;
      if (editForm.category && cat) {
        if (editForm.category !== cat.primary?.slug) {
          // Backend still stores/validates a flat categories shape on write —
          // only the read response nests it into primary/secondary.
          payload.categories = {
            primary_slug: editForm.category,
            secondary_slugs: (cat.secondary ?? []).map((s) => s.slug),
            subcategory_slugs: [...(cat.primary?.sub_slugs ?? [])],
          };
          payload.category_slug = editForm.category;
        } else {
          payload.category_slug = editForm.category;
        }
      }
      // `editPricingExempt` is computed once at component scope (see its definition
      // near the edit-form state) so this payload and the Pricing section of the edit
      // modal can never disagree about whether pricing applies. It covers both the
      // media-group case (DealMedia requires pricing to be absent entirely) and the
      // admin-flagged pricing_required === false category case.
      if (!editPricingExempt) {
        const orig = parseFloat(editForm.original_price);
        const disc = editForm.discounted_same_as_original ? orig : parseFloat(editForm.discounted_price);
        if (!Number.isNaN(orig)) payload.original_price = orig;
        if (!Number.isNaN(disc)) payload.discounted_price = disc;
        const existingCurrency = ((editingItem.pricing as { currency?: string } | undefined)?.currency || "").toUpperCase();
        if (editForm.currency && editForm.currency !== existingCurrency) payload.currency = editForm.currency;
      }

      // Validity — the backend does a shallow top-level merge (`{...existing, ...partial}`),
      // so `validity` must be sent as one fully-merged object or unrelated fields
      // (booking_deadline, travel_period, existing dates/validity_periods) get wiped.
      const existingValidity = (editingItem.validity as Record<string, unknown> | undefined) ?? {};
      const parsedDates = editForm.dates.filter(Boolean);
      const validityChanged = parsedDates.length > 0 || editForm.validity_periods.length > 0;
      if (validityChanged) {
        payload.validity = {
          ...existingValidity,
          dates: parsedDates.length > 0 ? parsedDates : (existingValidity.dates ?? []),
          validity_periods: editForm.validity_periods.length > 0 ? editForm.validity_periods : (existingValidity.validity_periods ?? []),
        };
      }

      // Tags — top-level, declared on TypedDealUpdateRequest
      const parsedTags = editForm.tags.split(",").map(t => t.trim()).filter(Boolean);
      if (parsedTags.length > 0) payload.tags = parsedTags;

      // Destination
      const cities = editForm.cities.split(",").map(c => c.trim()).filter(Boolean);
      const states = editForm.states.split(",").map(s => s.trim()).filter(Boolean);
      const countries = editForm.countries.split(",").map(c => c.trim()).filter(Boolean);
      if (cities.length > 0 || states.length > 0 || countries.length > 0) {
        payload.destination = { cities, states, countries };
      }

      // Inclusions / exclusions / highlights / pax — these live on different
      // nested blocks per category (experiences/packages have dedicated fields;
      // other categories don't, so we fall back to `extras` rather than send an
      // undeclared top-level key the schema would reject).
      const parsedInclusions = editForm.inclusions.filter(Boolean);
      const parsedExclusions = editForm.exclusions.filter(Boolean);
      const parsedHighlights = editForm.highlights.filter(Boolean);
      const minPax = parseInt(editForm.min_pax, 10);
      const maxPax = parseInt(editForm.max_pax, 10);
      const hasMinPax = !Number.isNaN(minPax) && minPax > 0;
      const hasMaxPax = !Number.isNaN(maxPax) && maxPax > 0;
      const groupForLists = (editingItem.categories as Record<string, unknown> | undefined)?.group_slug || editGroupSlug;

      if (groupForLists === "experiences") {
        if (parsedInclusions.length || parsedExclusions.length || parsedHighlights.length || hasMinPax || hasMaxPax) {
          const existingExp = (editingItem.experience as Record<string, unknown> | undefined) ?? {};
          payload.experience = {
            ...existingExp,
            ...(parsedInclusions.length ? { inclusions: parsedInclusions } : {}),
            ...(parsedExclusions.length ? { exclusions: parsedExclusions } : {}),
            ...(parsedHighlights.length ? { highlights: parsedHighlights } : {}),
            ...((hasMinPax || hasMaxPax) ? {
              group_size: {
                ...((existingExp.group_size as Record<string, unknown>) ?? {}),
                ...(hasMinPax ? { min: minPax } : {}),
                ...(hasMaxPax ? { max: maxPax } : {}),
              },
            } : {}),
          };
        }
      } else if (groupForLists === "packages") {
        if (parsedInclusions.length || parsedExclusions.length || parsedHighlights.length) {
          const existingPkg = (editingItem.package as Record<string, unknown> | undefined) ?? {};
          payload.package = {
            ...existingPkg,
            ...(parsedInclusions.length ? { inclusions: parsedInclusions } : {}),
            ...(parsedExclusions.length ? { exclusions: parsedExclusions } : {}),
            ...(parsedHighlights.length ? { highlights: parsedHighlights } : {}),
          };
        }
      } else {
        // No dedicated schema field for this category — preserve as labeled
        // extras entries instead of silently dropping the seller's input.
        const existingExtras = Array.isArray(editingItem.extras) ? [...(editingItem.extras as { name: string; value: unknown }[])] : [];
        const setExtra = (name: string, value: string) => {
          const idx = existingExtras.findIndex((e) => e.name === name);
          if (idx >= 0) existingExtras[idx] = { name, value };
          else existingExtras.push({ name, value });
        };
        let extrasChanged = false;
        if (parsedInclusions.length) { setExtra("inclusions", parsedInclusions.join("; ")); extrasChanged = true; }
        if (parsedExclusions.length) { setExtra("exclusions", parsedExclusions.join("; ")); extrasChanged = true; }
        if (parsedHighlights.length) { setExtra("highlights", parsedHighlights.join("; ")); extrasChanged = true; }
        if (extrasChanged) payload.extras = existingExtras;
      }

      // `terms.min_pax`/`terms.max_pax` now live on the base schema for every
      // group — send them there instead of stuffing them into `extras` (which
      // used to be the only place non-experience/package deals could put them).
      const existingTerms = (editingItem.terms as Record<string, unknown> | undefined) ?? {};
      const parsedAdvanceBooking = editForm.advance_booking.trim();
      const parsedCancellationPolicy = editForm.cancellation_policy.trim();
      const parsedBlackoutDates = editForm.blackout_dates.split(",").map((d) => d.trim()).filter(Boolean);
      const termsPatch: Record<string, unknown> = { ...existingTerms };
      if (hasMinPax) termsPatch.min_pax = minPax;
      if (hasMaxPax) termsPatch.max_pax = maxPax;
      if (parsedAdvanceBooking) termsPatch.advance_booking = parsedAdvanceBooking;
      if (parsedCancellationPolicy) termsPatch.cancellation_policy = parsedCancellationPolicy;
      if (parsedBlackoutDates.length) termsPatch.blackout_dates = parsedBlackoutDates;
      if (Object.keys(termsPatch).length) payload.terms = termsPatch;

      // Inventory — seller-managed manual counts (no auto-decrement; see plan doc).
      const parsedTotal = parseInt(editForm.inventory_total, 10);
      const parsedRemaining = parseInt(editForm.inventory_remaining, 10);
      const parsedCancelled = parseInt(editForm.inventory_cancelled, 10);
      const hasTotal = !Number.isNaN(parsedTotal) && parsedTotal >= 0;
      const hasRemaining = !Number.isNaN(parsedRemaining) && parsedRemaining >= 0;
      const hasCancelled = !Number.isNaN(parsedCancelled) && parsedCancelled >= 0;
      if (hasTotal || hasRemaining || hasCancelled || editForm.inventory_unit) {
        const existingInventory = (editingItem.inventory as Record<string, unknown> | undefined) ?? {};
        payload.inventory = {
          ...existingInventory,
          ...(hasTotal ? { total: parsedTotal } : {}),
          ...(hasRemaining ? { remaining: parsedRemaining } : {}),
          ...(hasCancelled ? { cancelled: parsedCancelled } : {}),
          ...(editForm.inventory_unit ? { unit: editForm.inventory_unit } : {}),
        };
      }

      // Curation flags.
      payload.is_flash = editForm.is_flash;
      if (editForm.is_flash && editForm.flash_ends_at) {
        payload.flash_ends_at = new Date(editForm.flash_ends_at).toISOString();
      } else if (!editForm.is_flash) {
        payload.flash_ends_at = null;
      }
      const parsedRailTags = editForm.rail_tags.split(",").map((t) => t.trim()).filter(Boolean);
      payload.rail_tags = parsedRailTags;

      // Category-specific details — merge into the existing nested block so
      // unrelated sub-fields (schedule, cabins, amenities, etc.) survive.
      const group = (editingItem.categories as Record<string, unknown> | undefined)?.group_slug || editGroupSlug;
      if (group === "stays" && (editForm.property_name || editForm.property_type)) {
        payload.stay = {
          ...(editingItem.stay ?? {}),
          ...(editForm.property_name ? { property_name: editForm.property_name } : {}),
          ...(editForm.property_type ? { property_type: editForm.property_type } : {}),
        };
      }
      if (group === "flights" && (editForm.airline_name || editForm.trip_type)) {
        payload.flight = {
          ...(editingItem.flight ?? {}),
          ...(editForm.airline_name ? { airline: { ...((editingItem.flight as Record<string,unknown> | undefined)?.airline as Record<string,unknown> ?? {}), name: editForm.airline_name } } : {}),
          ...(editForm.trip_type ? { trip_type: editForm.trip_type } : {}),
        };
      }
      if (group === "cruises" && (editForm.ship_name || editForm.cruise_line || editForm.embarkation_port || editForm.disembarkation_port)) {
        payload.cruise = {
          ...(editingItem.cruise ?? {}),
          ...((editForm.ship_name || editForm.cruise_line) ? {
            ship: {
              ...((editingItem.cruise as Record<string,unknown> | undefined)?.ship as Record<string,unknown> ?? {}),
              ...(editForm.ship_name ? { name: editForm.ship_name } : {}),
              ...(editForm.cruise_line ? { cruise_line: editForm.cruise_line } : {}),
            },
          } : {}),
          ...(editForm.embarkation_port ? { embarkation_port: editForm.embarkation_port } : {}),
          ...(editForm.disembarkation_port ? { disembarkation_port: editForm.disembarkation_port } : {}),
        };
      }
      if ((group === "transfers" || group === "transport") && (editForm.vehicle_type || editForm.vehicle_model)) {
        payload.transfer = {
          ...(editingItem.transfer ?? {}),
          vehicle: {
            ...((editingItem.transfer as Record<string,unknown> | undefined)?.vehicle as Record<string,unknown> ?? {}),
            ...(editForm.vehicle_type ? { vehicle_type: editForm.vehicle_type } : {}),
            ...(editForm.vehicle_model ? { vehicle_model: editForm.vehicle_model } : {}),
          },
        };
      }
      if (group === "experiences" && editForm.experience_type) {
        payload.experience = {
          ...(editingItem.experience ?? {}),
          ...((payload.experience as Record<string, unknown> | undefined) ?? {}),
          experience_type: editForm.experience_type,
        };
      }

      // Apply pending gallery removals and merge in the just-uploaded keys.
      // The gallery route already $addToSet the new keys, but if we send a
      // media.gallery here to apply removals we must keep those new keys too,
      // or the PUT would overwrite them back out.
      if (pendingGalleryRemovals.length > 0 || uploadedGalleryKeys.length > 0) {
        const currentGallery = (editingItem.media?.gallery ?? []).map((g) => g.key);
        const keptGallery = currentGallery.filter(
          (u) => !pendingGalleryRemovals.includes(resolveImageUrl(u) ?? u)
        );
        const newGallery = [...new Set([...keptGallery, ...uploadedGalleryKeys])];
        payload.media = { ...(editingItem.media ?? {}), gallery: newGallery };
      }

      // Slug / type / package settings — merged in so a single save covers everything.
      const origGroup = cat?.primary?.group_slug || "";
      const groupChanged = editGroupSlug && editGroupSlug !== origGroup;
      payload.categories = {
        subcategory_slugs: [...(cat?.primary?.sub_slugs ?? [])],
        ...((payload.categories as Record<string, unknown>) ?? {}),
        primary_slug: editPrimarySlug.trim(),
        secondary_slugs: editSecondarySlug.filter(Boolean),
        package_primary_slug: editPkgPrimarySlug.trim() || undefined,
        package_secondary_slugs: editPkgSecondarySlugs.filter(Boolean),
      };
      payload.category_slug = editPrimarySlug.trim();
      payload.is_in_packages = editIsInPackages;
      if (groupChanged) payload.retype_to_group = editGroupSlug;

      const updated = await dealsService.updateInventoryItem(dealCollection(editingItem), dealId(editingItem), payload);
      mergeEditedItem(updated.document);
      closeEditModal();
      await fetchMyInventory();
    } catch (err: unknown) {
      console.error("Failed to update item:", err);
      setSaveError(describeDealError(err, "Failed to save changes. Please try again."));
    } finally {
      setIsSaving(false);
      setUploadingMedia(false);
    }
  };

  const applyAiSuggestion = async (field: "title" | "description") => {
    if (!editingItem) return;

    const nextValue =
      field === "title"
        ? editingItem.ai_suggested_title
        : editingItem.ai_suggested_description;

    if (!nextValue) return;

    setIsApplyingAiField(field);
    setSaveError(null);

    try {
      const updated = await dealsService.updateInventoryItem(dealCollection(editingItem), dealId(editingItem), { [field]: nextValue });

      const mergedItem: Deal = {
        ...editingItem,
        ...updated.document,
        collection: updated.collection || editingItem.collection,
        [field]: nextValue,
        ...(field === "title"
          ? { ai_used_suggested_title: true }
          : { ai_used_suggested_description: true }),
      };

      mergeEditedItem(mergedItem);
      setEditForm((current) => ({
        ...current,
        [field]: nextValue,
      }));
    } catch (err: unknown) {
      console.error(`Failed to apply AI ${field}:`, err);
      setSaveError(`Failed to apply AI ${field}. Please try again.`);
    } finally {
      setIsApplyingAiField(null);
    }
  };

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [publishBlockedError, setPublishBlockedError] = useState<{
    code: string;
    message: string;
    onboarding_step_key?: string | null;
    pending_countries?: string[];
  } | null>(null);

  useBodyScrollLock(
    !!newsDeleteId ||
    !!editingItem ||
    !!publishBlockedError ||
    !!confirmDeleteDeal ||
    showGalleryDeleteConfirm
  );

  const handleToggleActive = async (item: Deal) => {
    const currentIsActive = item.lifecycle?.is_active !== false;
    const newIsActive = !currentIsActive;
    const id = dealId(item);
    setTogglingId(id);
    try {
      await dealsService.updateInventoryItem(dealCollection(item), id, { lifecycle: { is_active: newIsActive } });
      await fetchMyInventory();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (
        detail && typeof detail === "object" && "code" in detail &&
        ["market_not_covered", "market_pending_review", "last_minute_pending_review", "dates_expired_pending_review"].includes(
          (detail as { code?: string }).code ?? ""
        )
      ) {
        setPublishBlockedError(detail as typeof publishBlockedError);
      } else {
        console.error("Failed to toggle active state:", err);
      }
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (item: Deal) => {
    setConfirmDeleteDeal(item);
  };

  const confirmAndDelete = async (item: Deal) => {
    setConfirmDeleteDeal(null);
    setIsDeleting(true);
    setSaveError(null);
    try {
      await dealsService.deleteInventoryItem(item._stuck_shadow_deal ? "deals" : dealCollection(item), dealId(item));
      if (editingItem && dealId(editingItem) === dealId(item)) {
        closeEditModal();
      }
      await fetchMyInventory();
    } catch (err: unknown) {
      console.error("Failed to delete item:", err);
      setSaveError("Failed to delete this item. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // ─── Computed values for live discount badge ───────────────────────────────
  const pct = discountPct(editForm.original_price, editForm.discounted_price);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page-shell">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-gray-500 hover:text-gray-700">← Back</button>
            <h1 className="text-2xl font-bold text-gray-900">My Inventory</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/deals/create")}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
            >
              <span className="text-base">＋</span> Create New Deal
            </button>
            <button onClick={logout} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-4">
          <h2 className="text-gray-500 font-medium">Manage your extracted deals, packages, flights, and hotels.</h2>
        </div>

        {/* Seller recommendation signals */}
        <SellerScoreWidget score={sellerScore} status={sellerPrefStatus} />

        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveSection("deals")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSection === "deals" ? "bg-blue-600 text-white" : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"}`}
          >
            Deals
          </button>
          <button
            onClick={() => setActiveSection("webinars")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSection === "webinars" ? "bg-rose-600 text-white" : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"}`}
          >
            Webinars
          </button>
          <button
            onClick={() => setActiveSection("contacts")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSection === "contacts" ? "bg-green-600 text-white" : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"}`}
          >
            Contacts
          </button>
          <button
            onClick={() => setActiveSection("news")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSection === "news" ? "bg-amber-600 text-white" : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"}`}
          >
            News {myNews.length > 0 && <span className="ml-1 text-xs opacity-80">({myNews.length})</span>}
          </button>
        </div>

        {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700">{error}</div>}

        <section className="mb-8 bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Seller Analytics</h2>
              <p className="text-sm text-gray-500">Live buyer engagement, conversion funnel, AI usage, and top listings.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {showAnalytics && ([7, 30, 90] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => { setDashboardDays(d); void fetchSellerAnalytics(d); }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${dashboardDays === d ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"}`}
                >
                  {d}d
                </button>
              ))}
              {showAnalytics && (
                <button
                  onClick={() => void fetchSellerAnalytics(dashboardDays)}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Refresh
                </button>
              )}
              <button
                onClick={() => {
                  const next = !showAnalytics;
                  setShowAnalytics(next);
                  localStorage.setItem("inv_show_analytics", String(next));
                }}
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1"
              >
                {showAnalytics ? (
                  <><span>Hide Analytics</span><span>▲</span></>
                ) : (
                  <><span>Show Analytics</span><span>▼</span></>
                )}
              </button>
            </div>
          </div>
          {showAnalytics && (analyticsError ? (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{analyticsError}</div>
          ) : isAnalyticsLoading && !sellerDashboard ? (
            <div className="py-10 text-center text-gray-600">Loading analytics...</div>
          ) : sellerDashboard ? (
            <div className="space-y-6">

              {/* ENGAGEMENT TILES */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Engagement</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
                  {[
                    { label: "Views", value: sellerDashboard.totals.deal_views, sub: `${sellerDashboard.totals.deal_views_unique} unique`, border: "border-indigo-200", bg: "bg-indigo-50", text: "text-indigo-700", bold: "text-indigo-900" },
                    { label: "Likes", value: sellerDashboard.totals.deal_likes, border: "border-pink-200", bg: "bg-pink-50", text: "text-pink-700", bold: "text-pink-900" },
                    { label: "Saves", value: sellerDashboard.totals.deal_saves, sub: `${sellerDashboard.totals.deal_unsaves} unsaved`, border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-700", bold: "text-amber-900" },
                    { label: "WhatsApp", value: sellerDashboard.totals.whatsapp_clicks, border: "border-green-200", bg: "bg-green-50", text: "text-green-700", bold: "text-green-900" },
                    { label: "Email clicks", value: sellerDashboard.totals.email_clicks, border: "border-blue-200", bg: "bg-blue-50", text: "text-blue-700", bold: "text-blue-900" },
                    { label: "DM threads", value: sellerDashboard.totals.dm_threads_opened, border: "border-purple-200", bg: "bg-purple-50", text: "text-purple-700", bold: "text-purple-900" },
                  ].map((tile) => (
                    <div key={tile.label} className={`rounded-xl border ${tile.border} ${tile.bg} p-4`}>
                      <p className={`text-xs uppercase tracking-wide ${tile.text}`}>{tile.label}</p>
                      <p className={`mt-1.5 text-2xl font-bold ${tile.bold}`}>{tile.value.toLocaleString()}</p>
                      {tile.sub && <p className="text-xs text-gray-500 mt-0.5">{tile.sub}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* CONVERSIONS TILES */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Conversions</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
                  {[
                    { label: "Enquiries Received", value: sellerDashboard.totals.enquiries, border: "border-orange-200", bg: "bg-orange-50", text: "text-orange-700", bold: "text-orange-900" },
                    { label: "🕐 Open", value: sellerDashboard.totals.enquiries - (sellerDashboard.totals.leads_accepted ?? 0) - (sellerDashboard.totals.leads_contacted ?? 0) - (sellerDashboard.totals.leads_closed ?? 0), border: "border-yellow-200", bg: "bg-yellow-50", text: "text-yellow-700", bold: "text-yellow-900" },
                    { label: "✅ Accepted", value: sellerDashboard.totals.leads_accepted, border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", bold: "text-emerald-900" },
                    { label: "💬 Contacted", value: sellerDashboard.totals.leads_contacted ?? 0, border: "border-blue-200", bg: "bg-blue-50", text: "text-blue-700", bold: "text-blue-900" },
                    { label: "🔒 Closed", value: sellerDashboard.totals.leads_closed ?? 0, border: "border-gray-200", bg: "bg-gray-50", text: "text-gray-600", bold: "text-gray-800" },
                    { label: "DM Threads", value: sellerDashboard.totals.dm_threads_opened, border: "border-violet-200", bg: "bg-violet-50", text: "text-violet-700", bold: "text-violet-900" },
                  ].map((tile) => (
                    <div key={`conv-${tile.label}`} className={`rounded-xl border ${tile.border} ${tile.bg} p-4`}>
                      <p className={`text-xs uppercase tracking-wide ${tile.text}`}>{tile.label}</p>
                      <p className={`mt-1.5 text-2xl font-bold ${tile.bold}`}>{Math.max(0, tile.value).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* FUNNEL */}
              {(() => {
                const t = sellerDashboard.totals;
                const steps = [
                  { label: "Views", count: t.deal_views, color: "bg-indigo-500" },
                  { label: "Likes", count: t.deal_likes, color: "bg-pink-500" },
                  { label: "Saves", count: t.deal_saves, color: "bg-amber-500" },
                  { label: "Enquiries", count: t.enquiries, color: "bg-orange-500" },
                  { label: "Accepted", count: t.leads_accepted, color: "bg-emerald-500" },
                  { label: "Contacted", count: t.leads_contacted ?? 0, color: "bg-blue-500" },
                ];
                const max = Math.max(...steps.map((s) => s.count), 1);
                return (
                  <div className="rounded-xl border border-gray-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Conversion Funnel (last {dashboardDays}d)</p>
                    <div className="space-y-2">
                      {steps.map((step, i) => {
                        const prev = i > 0 ? steps[i - 1].count : step.count;
                        const pct = prev > 0 ? Math.round((step.count / prev) * 100) : 0;
                        const barW = Math.round((step.count / max) * 100);
                        return (
                          <div key={step.label} className="flex items-center gap-3">
                            <span className="w-20 text-xs text-gray-600 text-right shrink-0">{step.label}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                              <div className={`${step.color} h-5 rounded-full transition-all`} style={{ width: `${barW}%` }} />
                            </div>
                            <span className="w-16 text-xs font-semibold text-gray-800 shrink-0">{step.count.toLocaleString()}</span>
                            {i > 0 && <span className="w-12 text-xs text-gray-400 shrink-0">{pct}%</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* DAILY TREND CHART */}
              {sellerDashboard.days.length > 1 && (() => {
                type ChartSeries = { key: keyof typeof sellerDashboard.days[0]; label: string; color: string; stroke: string };
                const series: ChartSeries[] = [
                  { key: "deal_views",    label: "Views",     color: "#6366f1", stroke: "#6366f1" },
                  { key: "enquiries",     label: "Enquiries", color: "#f97316", stroke: "#f97316" },
                  { key: "whatsapp_clicks", label: "WhatsApp", color: "#22c55e", stroke: "#22c55e" },
                  { key: "deal_likes",    label: "Likes",     color: "#ec4899", stroke: "#ec4899" },
                ];
                const activeSeries = chartActiveSeries;
                const setActiveSeries = setChartActiveSeries;
                const days = sellerDashboard.days.slice().sort((a, b) => a.date.localeCompare(b.date));
                const W = 700, H = 180, PL = 36, PR = 8, PT = 12, PB = 28;
                const innerW = W - PL - PR, innerH = H - PT - PB;
                const allVals = days.flatMap(d => series.filter(s => activeSeries.has(s.key)).map(s => Number((d as unknown as Record<string, unknown>)[s.key] ?? 0)));
                const maxVal = Math.max(...allVals, 1);
                const xStep = innerW / Math.max(days.length - 1, 1);
                const yScale = (v: number) => PT + innerH - (v / maxVal) * innerH;
                const xScale = (i: number) => PL + i * xStep;
                const labelEvery = Math.max(1, Math.ceil(days.length / 7));
                return (
                  <div className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Daily Trend (last {dashboardDays}d)</p>
                      <div className="flex gap-3 flex-wrap">
                        {series.map(s => (
                          <button
                            key={s.key}
                            onClick={() => setActiveSeries(prev => {
                              const next = new Set(prev);
                              next.has(s.key) ? next.delete(s.key) : next.add(s.key);
                              return next;
                            })}
                            className="flex items-center gap-1.5 text-xs"
                          >
                            <span className="w-3 h-3 rounded-full inline-block" style={{ background: activeSeries.has(s.key) ? s.color : "#d1d5db" }} />
                            <span className={activeSeries.has(s.key) ? "text-gray-700 font-medium" : "text-gray-400"}>{s.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 180 }}>
                      {/* Y gridlines */}
                      {[0, 0.25, 0.5, 0.75, 1].map(frac => {
                        const y = PT + innerH * (1 - frac);
                        return (
                          <g key={frac}>
                            <line x1={PL} x2={W - PR} y1={y} y2={y} stroke="#f3f4f6" strokeWidth="1" />
                            <text x={PL - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#9ca3af">
                              {frac === 0 ? "0" : Math.round(maxVal * frac).toLocaleString()}
                            </text>
                          </g>
                        );
                      })}
                      {/* X axis labels */}
                      {days.map((d, i) => i % labelEvery === 0 && (
                        <text key={d.date} x={xScale(i)} y={H - 4} textAnchor="middle" fontSize="9" fill="#9ca3af">
                          {d.date.slice(5)}
                        </text>
                      ))}
                      {/* Lines */}
                      {series.filter(s => activeSeries.has(s.key)).map(s => {
                        const pts = days.map((d, i) => `${xScale(i)},${yScale(Number((d as unknown as Record<string, unknown>)[s.key] ?? 0))}`).join(" ");
                        return (
                          <g key={s.key}>
                            <polyline points={pts} fill="none" stroke={s.stroke} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
                            {days.map((d, i) => {
                              const v = Number((d as unknown as Record<string, unknown>)[s.key] ?? 0);
                              return v > 0 ? (
                                <circle key={i} cx={xScale(i)} cy={yScale(v)} r="2.5" fill={s.stroke}>
                                  <title>{d.date}: {v.toLocaleString()}</title>
                                </circle>
                              ) : null;
                            })}
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                );
              })()}

              {/* CONTENT + AI USAGE */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="rounded-xl border border-gray-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Content (News &amp; Webinars)</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: "News views", value: sellerDashboard.totals.news_views },
                      { label: "News likes", value: sellerDashboard.totals.news_likes },
                      { label: "Webinar views", value: sellerDashboard.totals.webinar_views },
                      { label: "Webinar likes", value: sellerDashboard.totals.webinar_likes },
                    ].map((item) => (
                      <div key={item.label} className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                        <p className="text-xs text-gray-500">{item.label}</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{(item.value ?? 0).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">AI Usage</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: "Flyers", value: sellerDashboard.totals.ai_flyers_generated },
                      { label: "Itineraries", value: sellerDashboard.totals.ai_itineraries_generated },
                      { label: "Videos", value: sellerDashboard.totals.ai_videos_generated },
                      { label: "Slides", value: sellerDashboard.totals.ai_slides_generated },
                    ].map((item) => (
                      <div key={item.label} className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                        <p className="text-xs text-gray-500">{item.label}</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{(item.value ?? 0).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-lg bg-indigo-50 border border-indigo-200 p-3 flex items-center justify-between">
                    <p className="text-xs text-indigo-700">Total tokens used</p>
                    <p className="text-lg font-bold text-indigo-900">{(sellerDashboard.totals.ai_tokens_used ?? 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* TOP LISTINGS from deal_metrics (via legacy analytics) */}
              {sellerAnalytics && sellerAnalytics.top_items.length > 0 && (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h3 className="font-semibold text-gray-900">Top Listings</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-white">
                        <tr className="text-left border-b">
                          <th className="px-4 py-3">Listing</th>
                          <th className="px-4 py-3">WhatsApp</th>
                          <th className="px-4 py-3">Email</th>
                          <th className="px-4 py-3">Saves</th>
                          <th className="px-4 py-3">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerAnalytics.top_items.map((item) => (
                          <tr key={`${item.item_type}-${item.item_id}`} className="border-b last:border-b-0 align-top">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900">{item.item_title || "Untitled listing"}</p>
                              <p className="text-xs text-gray-500">{item.item_type}</p>
                            </td>
                            <td className="px-4 py-3 text-green-700 font-semibold">{item.whatsapp_clicks}</td>
                            <td className="px-4 py-3 text-blue-700 font-semibold">{item.email_clicks}</td>
                            <td className="px-4 py-3 text-amber-700 font-semibold">{item.saves}</td>
                            <td className="px-4 py-3 font-semibold text-gray-900">{item.total_events}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* RECENT ACTIVITY */}
              {sellerAnalytics && sellerAnalytics.recent_events.length > 0 && (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h3 className="font-semibold text-gray-900">Recent Buyer Activity</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-white">
                        <tr className="text-left border-b">
                          <th className="px-4 py-3">Buyer</th>
                          <th className="px-4 py-3">Action</th>
                          <th className="px-4 py-3">Listing</th>
                          <th className="px-4 py-3">Source</th>
                          <th className="px-4 py-3">When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerAnalytics.recent_events.map((event, index) => (
                          <tr key={`${event.item_id}-${event.created_at || index}-${event.event_type}`} className="border-b last:border-b-0 align-top">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900">{formatBuyerLabel(event)}</p>
                              <p className="text-xs text-gray-500">{event.buyer?.email || event.buyer?.phone || event.buyer?.whatsapp_number || ""}</p>
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-800">{formatEventLabel(event)}</td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900">{event.item_title || "Untitled listing"}</p>
                              <p className="text-xs text-gray-500">{event.item_type}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{event.source}</td>
                            <td className="px-4 py-3 text-gray-600">{event.created_at ? formatISTDateTime(event.created_at) : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </div>
          ) : (
            <div className="p-4 text-sm text-gray-500">No analytics data yet — interactions will appear here once buyers engage with your listings.</div>
          ))}
        </section>

        {activeSection === "webinars" ? (
          webinars.length === 0 ? (
            <div className="py-14 text-center bg-white rounded-xl border border-gray-200 text-gray-600">
              No webinars found.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {webinars.map((webinar) => (
                <article key={webinar.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  <div
                    className="h-40 bg-white border-b border-gray-100 cursor-zoom-in relative group"
                    onClick={() => setPreviewWebinar(webinar)}
                    title="Click to preview"
                  >
                    {webinar.thumbnail_url ? (
                      <img src={webinar.thumbnail_url} alt={webinar.title} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl">📹</div>
                    )}
                    {(webinar.video_url || webinar.recording_url) && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="w-12 h-12 rounded-full bg-black/50 text-white flex items-center justify-center text-xl group-hover:bg-black/70 transition-colors">▶</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-base font-semibold text-gray-900 line-clamp-1">{webinar.title}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${webinar.is_own_webinar ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                        {webinar.owner_label || "webinar"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{webinar.description || "No description"}</p>
                    <div className="mt-3 text-xs text-gray-500 space-y-1">
                      <p>Host: {webinar.host_name || "-"}</p>
                      <p>Date: {webinar.webinar_date || "-"}</p>
                      <p>Status: {webinar.status || "-"}</p>
                    </div>
                    <button
                      onClick={() => setPreviewWebinar(webinar)}
                      className="mt-3 w-full py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      🔍 Preview
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )
        ) : activeSection === "contacts" ? (
          contacts.length === 0 ? (
            <div className="py-14 text-center bg-white rounded-xl border border-gray-200 text-gray-600">
              No contacts found for your account.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contacts.map((contact) => (
                <article key={contact.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-base font-semibold text-gray-900">{contact.contact_name}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase bg-green-100 text-green-700">
                      own contact
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{contact.designation || "No designation"}</p>
                  <div className="mt-3 text-xs text-gray-600 space-y-1">
                    <p>Organization: {contact.organization_name}</p>
                    <p>Type: {contact.organization_type}</p>
                    <p>Vertical: {contact.vertical || "-"}</p>
                    <p>Email: {contact.emails?.[0] || "-"}</p>
                    <p>Phone: {contact.phone_numbers?.[0] || "-"}</p>
                    <p>City: {contact.city || "-"}</p>
                    <p>Region: {contact.region || "-"}</p>
                    <p>Department: {contact.department || "-"}</p>
                  </div>
                  {contact.description ? (
                    <p className="mt-3 text-sm text-gray-500 line-clamp-3">{contact.description}</p>
                  ) : null}
                </article>
              ))}
            </div>
          )
        ) : activeSection === "news" ? (
          newsLoading ? (
            <div className="py-16 text-center text-gray-600">Loading your news...</div>
          ) : myNews.length === 0 ? (
            <div className="py-14 text-center bg-white rounded-xl border border-gray-200 text-gray-600">
              <div className="text-4xl mb-3">📰</div>
              <p className="font-medium">No news submitted yet.</p>
              <button
                onClick={() => navigate("/news/add")}
                className="mt-4 px-5 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
              >
                + Add News
              </button>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myNews.map((article) => (
                <article key={article.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col">
                  {/* Cover image */}
                  <div
                    className="h-44 bg-white border-b border-gray-100 flex-shrink-0 relative cursor-zoom-in"
                    onClick={() => setPreviewNews(article)}
                    title="Click to preview"
                  >
                    {(() => {
                      const src = article.cover_image_url || article.attachments?.find((a) => a.thumbnail_url)?.thumbnail_url;
                      return src
                        ? <img src={src} alt={article.title} className="w-full h-full object-contain" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                        : <div className="w-full h-full flex items-center justify-center text-4xl">📰</div>;
                    })()}
                  </div>

                  <div className="p-4 flex flex-col flex-1">
                    {article.category && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase bg-amber-100 text-amber-700 w-fit mb-1">
                        {article.category}
                      </span>
                    )}
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{article.title}</h3>
                    {article.summary && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{article.summary}</p>}

                    {/* Gallery thumbnails */}
                    {article.gallery && article.gallery.length > 0 && (
                      <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
                        {article.gallery.map((g, gi) => (
                          <img key={gi} src={g.url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-gray-200" />
                        ))}
                      </div>
                    )}

                    {/* Attachments */}
                    {article.attachments && article.attachments.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {article.attachments.map((att, ai) => (
                          <AttachmentLink
                            key={ai}
                            attachment={att}
                            className="flex items-center gap-2 text-xs text-blue-600 hover:underline"
                            iconClassName="w-8 h-8 object-cover rounded border border-gray-200 shrink-0"
                          />
                        ))}
                      </div>
                    )}

                    {/* Stats row */}
                    <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
                      <span>❤️ {article.like_count ?? 0}</span>
                      <span>👁 {article.view_count ?? 0}</span>
                      {article.gallery && article.gallery.length > 0 && <span>🖼 {article.gallery.length}</span>}
                      {article.attachments && article.attachments.length > 0 && <span>📎 {article.attachments.length}</span>}
                      <span className="ml-auto">{article.published_at ? formatISTMonthDayShort(article.published_at) : ""}</span>
                    </div>

                    {/* Action buttons */}
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => setPreviewNews(article)}
                        className="flex-1 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        🔍 Preview & Edit
                      </button>
                      <button
                        onClick={() => setNewsDeleteId(article.id)}
                        className="px-3 py-1.5 rounded-lg border border-red-200 text-xs font-medium text-red-500 hover:bg-red-50 transition-colors"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Delete confirm */}
            {newsDeleteId && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
                  <h3 className="text-base font-bold text-gray-900 mb-2">Delete article?</h3>
                  <p className="text-sm text-gray-500 mb-5">This cannot be undone.</p>
                  <div className="flex gap-3">
                    <button onClick={() => setNewsDeleteId(null)} className="flex-1 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button onClick={() => void handleDeleteNews(newsDeleteId)} className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700">Delete</button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit modal */}
            {editingNews && (
              <NewsEditModal
                article={editingNews}
                saving={newsSaving}
                error={newsEditError}
                onSave={handleSaveNews}
                onClose={() => { setEditingNews(null); setNewsEditError(""); }}
              />
            )}
          </>
        )
        ) : isLoading ? (
          <div className="py-16 text-center text-gray-600">Loading your inventory...</div>
        ) : inventory.length === 0 ? (
          <div className="py-24 text-center bg-white rounded-xl border-2 border-dashed border-gray-200">
            <div className="text-5xl mb-4">🤝</div>
            <h3 className="text-lg font-semibold text-gray-900">No deals yet</h3>
            <p className="text-gray-500 mt-1 text-sm">Create your first deal and it will appear here.</p>
            <button
              onClick={() => navigate("/deals/create")}
              className="mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              ＋ Create Your First Deal
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {inventory.map((item) => (
              <article
                key={`${item.item_type}-${dealId(item)}`}
                id={`inventory-item-${dealId(item)}`}
                className={`bg-white border rounded-xl overflow-hidden shadow-sm transition-all ${item.lifecycle?.is_active === false ? "border-gray-200 opacity-70" : "border-gray-200"} ${highlightedDealId === dealId(item) ? "ring-2 ring-indigo-500 ring-offset-2" : ""}`}
              >
                {(() => {
                  const slides = previewImagesForItem(item);
                  const videoUrl = isVideoItem(item) ? itemVideoUrl(item) : null;
                  const galleryVids = galleryVideosForItem(item).filter((v) => v !== videoUrl);
                  const itemKey = `${item.item_type}-${dealId(item)}`;
                  return (
                    <div
                      className="cursor-zoom-in"
                      onClick={() => setPreviewItem(item)}
                      title="Click to preview"
                    >
                      <CardSlideshow
                        key={itemKey}
                        slides={slides}
                        videoUrl={videoUrl || undefined}
                        extraVideos={galleryVids}
                        label={item.item_type}
                        title={item.title}
                      />
                    </div>
                  );
                })()}
                <div className="p-4">
                  {/* ── AI analysis job status badge ── */}
                  {(() => {
                    const id = dealId(item);
                    const job = bgJobs.get(`deal:${id}`);
                    if (!job) return null;
                    if (job.status === "completed") {
                      return (
                        <div className="mb-3 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                          <span className="text-green-600">✅</span>
                          <p className="text-xs text-green-700 font-medium">AI analysis complete</p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* ── Optional price nudge — pipeline finished but the deal has
                         no usable price anywhere in `pricing`. Dismissible,
                         never required, never gates publish. ── */}
                  {(() => {
                    const job = bgJobs.get(`deal:${dealId(item)}`);
                    if (job?.status !== "completed") return null;
                    if (item._stuck_shadow_deal) return null;
                    if (hasStructuredPrice(item.pricing, item)) return null;
                    return <PriceNudge item={item} onSaved={() => void fetchMyInventory()} />;
                  })()}

                  {/* ── AI analysis job status badge (failure states) ── */}
                  {(() => {
                    const id = dealId(item);
                    const job = bgJobs.get(`deal:${id}`);
                    if (!job) return null;
                    if (job.status === "failed") {
                      return (
                        <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                          <span className="text-red-600">❌</span>
                          <p className="text-xs text-red-700 font-medium">AI analysis failed</p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* ── Last-minute deal admin review banner ── */}
                  {item.admin_review?.status === "pending" && (
                    <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                      <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                      <p className="text-xs text-amber-700 font-medium">Last-minute deal — pending admin approval before it can go live.</p>
                    </div>
                  )}
                  {item.admin_review?.status === "rejected" && (
                    <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                      <p className="text-xs font-semibold text-red-800">Last-minute deal rejected by admin</p>
                      {item.admin_review.rejection_reason && (
                        <p className="text-xs text-red-700 leading-snug mt-0.5">{item.admin_review.rejection_reason}</p>
                      )}
                    </div>
                  )}

                  {/* ── Inactive / draft banner ── */}
                  {item.lifecycle?.is_active === false && item.admin_review?.status !== "pending" && (
                    <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                      <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                      <p className="text-xs text-amber-700 font-medium">Draft — not visible in feed. Click <strong>Publish →</strong> to go live.</p>
                    </div>
                  )}

                  {/* ── AI analysis failed / stuck shadow deal banner ── */}
                  {item._stuck_shadow_deal && (
                    <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3">
                      <p className="text-xs font-semibold text-red-800 mb-1">Analysis failed — needs manual fix</p>
                      <p className="text-xs text-red-700 leading-snug">
                        {item.needs_manual_recategorisation_reason || "AI couldn't classify this deal into a valid category."}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          onClick={() => void handleDelete(item)}
                          className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Seller type mismatch approval banner ── */}
                  {item.deal_record_status === "pending_seller_type_approval" && (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs font-semibold text-amber-800 mb-1">Seller profile update required</p>
                      <p className="text-xs text-amber-700 leading-snug">
                        AI classified this as a <strong>{(item as Record<string, unknown>).seller_type_approval_ai_group as string}</strong> deal,
                        but your seller type does not include that category. Update your profile to publish it.
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          onClick={() => navigate("/profile")}
                          className="px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                        >
                          Update Profile →
                        </button>
                        <button
                          onClick={() => void handleDelete(item)}
                          className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-start">
                    <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">{item.categories?.primary?.slug || "travel"}</p>
                    <span className="text-[10px] text-gray-400">{formatISTMonthDayShort(item.created_at || "")}</span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 mt-1 line-clamp-1">{item.title}</h2>

                  <div className="mt-3 flex items-center gap-3">
                    {(item.pricing as { discounted_price?: number } | undefined)?.discounted_price ? (
                      <span className="text-xl font-bold text-blue-700">{currencySymbol((item.pricing as { currency?: string } | undefined)?.currency)}{(item.pricing as { discounted_price?: number }).discounted_price}</span>
                    ) : (item.pricing as { original_price?: number } | undefined)?.original_price ? (
                      <span className="text-xl font-bold text-blue-700">{currencySymbol((item.pricing as { currency?: string } | undefined)?.currency)}{(item.pricing as { original_price?: number }).original_price}</span>
                    ) : (
                      <span className="text-sm font-medium text-gray-500 italic">No price set</span>
                    )}
                    {(item.pricing as { discounted_price?: number; original_price?: number } | undefined)?.discounted_price && (item.pricing as { discounted_price?: number; original_price?: number } | undefined)?.original_price ? (
                      <span className="text-sm text-gray-400 line-through">{currencySymbol((item.pricing as { currency?: string } | undefined)?.currency)}{(item.pricing as { original_price?: number }).original_price}</span>
                    ) : null}
                  </div>

                  {!item._stuck_shadow_deal && (
                  <div className="mt-4 flex items-center justify-between pt-4 border-t border-gray-50">
                    <button
                      onClick={() => handleLike(dealId(item), dealCollection(item))}
                      className="px-3 py-1.5 text-xs bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      Likes ({item.likes || 0})
                    </button>
                    <span className="px-3 py-1.5 text-xs bg-gray-50 rounded-lg text-gray-500">
                      🔖 Saves ({item.save_count || 0})
                    </span>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const isActive = item.lifecycle?.is_active !== false;
                        const isToggling = togglingId === dealId(item);
                        return (
                          <button
                            onClick={() => void handleToggleActive(item)}
                            disabled={isToggling}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-all ${
                              isToggling
                                ? "opacity-60 cursor-wait bg-gray-100 text-gray-400 border-gray-200"
                                : isActive
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                                : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200"
                            }`}
                            title={isActive ? "Click to deactivate (removes from feed)" : "Click to activate (adds back to feed)"}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${isToggling ? "bg-gray-400" : isActive ? "bg-emerald-500" : "bg-amber-400"}`} />
                            {isToggling ? "Updating…" : isActive ? "Live" : "Publish →"}
                          </button>
                        );
                      })()}
                      <button
                        onClick={() => handleDelete(item)}
                        className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        Delete
                      </button>
                      {item.lifecycle?.is_active !== false && (
                        item.is_boosted ? (
                          <button
                            onClick={() => navigate("/boost/campaigns")}
                            className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                            title="Deal is currently boosted — click to view campaign"
                          >
                            ⚡ Boosted{typeof item.boost_count === "number" && item.boost_count > 0 ? ` ×${item.boost_count}` : ""}
                          </button>
                        ) : (
                          <button
                            onClick={() => navigate(
                              `/boost/new?deal_id=${dealId(item)}&collection=${encodeURIComponent(dealCollection(item))}&title=${encodeURIComponent(item.title ?? "")}`
                            )}
                            className="px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 border border-amber-200 transition-colors"
                            title="Boost this deal"
                          >
                            ⚡ Boost
                          </button>
                        )
                      )}
                      <button
                        onClick={() => setPreviewItem(item)}
                        className="px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Preview & Edit →
                      </button>
                    </div>
                  </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
        {inventoryHasMore && !isLoading && activeSection === "deals" && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => void fetchMyInventory(inventorySkip, true)}
              disabled={inventoryLoadingMore}
              className="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-60"
            >
              {inventoryLoadingMore ? "Loading…" : "Load more deals"}
            </button>
          </div>
        )}
      </main>

      {/* ── Full-screen media previews ──────────────────────────────────────── */}
      {previewItem && (
        <MediaLightbox
          title={previewItem.title}
          media={dealLightboxMedia(previewItem)}
          onClose={() => setPreviewItem(null)}
          onEdit={() => { const it = previewItem; setPreviewItem(null); openEditModal(it); }}
          editLabel="Edit Details"
        />
      )}
      {previewWebinar && (
        <MediaLightbox
          title={previewWebinar.title}
          media={webinarLightboxMedia(previewWebinar)}
          onClose={() => setPreviewWebinar(null)}
        />
      )}
      {previewNews && (
        <MediaLightbox
          title={previewNews.title}
          media={newsLightboxMedia(previewNews)}
          attachments={(previewNews.attachments ?? []).map((a) => ({ name: a.name, url: a.url, thumbnail_url: a.thumbnail_url }))}
          onClose={() => setPreviewNews(null)}
          onEdit={() => { const a = previewNews; setPreviewNews(null); setEditingNews(a); setNewsEditError(""); }}
          editLabel="Edit"
        />
      )}

      {/* ── Edit Modal ──────────────────────────────────────────────────────── */}
      {editingItem && (
        (() => {
          const locationText = formatItemLocation(editingItem);
          return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">

            {/* Saving overlay — blocks interaction during save or Pinecone re-index */}
            {isSaving && (
              <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-[2px] flex flex-col items-center justify-center gap-3 rounded-2xl">
                <svg className="w-8 h-8 animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <p className="text-sm font-semibold text-indigo-700">
                  Saving & indexing to Pinecone…
                </p>
              </div>
            )}

            {/* Modal header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Edit {editingItem.item_type}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">ID: {dealId(editingItem)}</p>
                </div>
                <button
                  onClick={closeEditModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Tab navigation */}
            <div className="flex gap-1 px-4 pt-3 border-b border-gray-100 overflow-x-auto">
              {([
                ["basic", "Basic Info"],
                ["pricing", "Pricing, Dates & Tags"],
                ["category", "Category Details"],
                ["media", "Media"],
                ["packaging", "Slugs & Packaging"],
              ] as [EditModalTab, string][]).map(([tab, label]) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setEditModalTab(tab)}
                  className={`px-3 py-2 text-xs font-semibold whitespace-nowrap rounded-t-lg border-b-2 transition-colors ${
                    editModalTab === tab
                      ? "border-blue-600 text-blue-700 bg-blue-50"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-5">
              {saveError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{saveError}</div>
              )}

              {/* ── Media gallery ─────────────────────────────────── */}
              {editModalTab === "media" && (() => {
                const images = previewImagesForItem(editingItem);
                const videos = galleryVideosForItem(editingItem);
                const mediaRec = editingItem.media as Record<string, unknown> | undefined;
                const cleanUrl = resolveImageUrl((mediaRec?.image_clean as string | undefined) || undefined);
                const primaryVideoUrl = itemVideoUrl(editingItem);
                // Primary = the cleaned analysis image when present; otherwise the
                // primary video; otherwise the first preview image. The primary
                // asset can never be deleted (the backend enforces this too).
                let primaryKind: "image" | "video" = "image";
                let primaryUrl: string | undefined;
                if (cleanUrl && images.includes(cleanUrl)) {
                  primaryKind = "image";
                  primaryUrl = cleanUrl;
                } else if (primaryVideoUrl) {
                  primaryKind = "video";
                  primaryUrl = primaryVideoUrl;
                } else {
                  primaryKind = "image";
                  primaryUrl = images[0];
                }
                const primaryImage = primaryKind === "image" ? primaryUrl : undefined;
                const restImages = images.filter((url) => url !== primaryImage);
                if (images.length === 0 && videos.length === 0) return null;
                return (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Media ({images.length + videos.length})
                    </p>
                    <p className="text-xs font-bold text-red-600 mb-2">
                      The primary and locked (🔒) images can’t be deleted. To delete a gallery image, hover over it and click ×, then click “Save changes” to continue.
                    </p>

                    {/* Primary image (clean) or first video */}
                    {primaryImage && (
                      <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-100 mb-2">
                        <img
                          src={primaryImage}
                          alt="Primary flyer"
                          className="w-full max-h-52 object-contain"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                        <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold bg-black/60 text-white rounded">
                          Primary
                        </span>
                      </div>
                    )}

                    {/* Image grid (gallery images) */}
                    {restImages.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        {restImages.map((url, i) => {
                          const galleryItem = galleryItemForUrl(editingItem, url);
                          const isGallery = url !== primaryUrl && Boolean(galleryItem);
                          const isPending = pendingGalleryRemovals.includes(url);
                          if (isPending) return null;
                          // Only gallery-route uploads are deletable. The original
                          // flyer and other analysis assets are "separate" — shown
                          // muted and locked (the backend rejects deleting them too).
                          return (
                            <div key={url} className="relative group">
                              <img
                                src={url}
                                alt={`Image ${i + 1}`}
                                className={`w-full h-16 object-cover rounded-lg border border-gray-200 ${isGallery ? "" : "opacity-50"}`}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                              />
                              {isGallery ? (
                                <button
                                  type="button"
                                  onClick={() => markGalleryForRemoval(url)}
                                  className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] font-bold leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                  title="Mark for removal"
                                >×</button>
                              ) : (
                                <span
                                  className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/50 text-white rounded-full text-[10px] leading-none flex items-center justify-center"
                                  title="Original asset — cannot be deleted"
                                >🔒</span>
                              )}
                              <GalleryStatusBadge item={galleryItem} />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Videos */}
                    {videos.map((vurl, i) => {
                      const isPrimaryVideo = primaryKind === "video" && vurl === primaryUrl;
                      const galleryVideoItem = galleryItemForUrl(editingItem, vurl);
                      const isGalleryVideo = !isPrimaryVideo && Boolean(galleryVideoItem);
                      const isPending = pendingGalleryRemovals.includes(vurl);
                      if (isPending) return null;
                      return (
                        <div key={vurl} className="relative group rounded-xl overflow-hidden border border-gray-200 bg-black mb-2">
                          <video src={vurl} className="w-full max-h-52 object-contain" controls muted playsInline />
                          <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold bg-black/60 text-white rounded">
                            {isPrimaryVideo ? "Primary" : `Video ${videos.length > 1 ? i + 1 : ""}`}
                          </span>
                          {isGalleryVideo && (
                            <button
                              type="button"
                              onClick={() => markGalleryForRemoval(vurl)}
                              className="absolute top-2 right-2 w-7 h-7 bg-red-600 text-white rounded-full text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              title="Mark for removal"
                            >×</button>
                          )}
                          <GalleryStatusBadge item={galleryVideoItem} />
                        </div>
                      );
                    })}

                    {editingItem.media?.pdf_url && (
                      <a
                        href={resolveImageUrl(editingItem.media.pdf_url || undefined)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex mt-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        View original PDF ↗
                      </a>
                    )}
                  </div>
                );
              })()}

              {/* ── Add more media ── */}
              {editModalTab === "media" && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Add to Gallery</p>

                {/* Queued (not-yet-uploaded) files — uploaded only on Save */}
                {pendingGalleryFiles.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {pendingGalleryFiles.map((p) => (
                      <div key={p.previewUrl} className="relative group">
                        {p.isVideo ? (
                          <video src={p.previewUrl} className="w-full h-16 object-cover rounded-lg border border-gray-200" muted playsInline />
                        ) : p.file.type.startsWith("image/") ? (
                          <img src={p.previewUrl} alt={p.file.name} className="w-full h-16 object-cover rounded-lg border border-gray-200" />
                        ) : (
                          <div className="w-full h-16 bg-gray-100 rounded-lg border border-gray-200 flex flex-col items-center justify-center gap-0.5 px-1">
                            <span className="text-lg leading-none">{p.file.type === "application/pdf" ? "📄" : "📝"}</span>
                            <span className="text-[9px] text-gray-500 truncate w-full text-center">{p.file.name}</span>
                          </div>
                        )}
                        <span className="absolute bottom-0.5 left-0.5 px-1 py-0.5 text-[9px] font-bold bg-amber-500 text-white rounded">
                          Pending
                        </span>
                        <button
                          type="button"
                          onClick={() => removePendingGalleryFile(p.previewUrl)}
                          disabled={uploadingMedia || isSaving}
                          className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] font-bold leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center disabled:opacity-50"
                          title="Remove from queue"
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                <label className={`flex flex-col items-center justify-center gap-1 w-full border-2 border-dashed rounded-xl py-4 cursor-pointer transition-colors ${uploadingMedia ? "border-blue-200 bg-blue-50 cursor-wait" : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"}`}>
                  <input
                    type="file"
                    accept={GALLERY_ACCEPT_ATTR}
                    multiple
                    className="hidden"
                    disabled={uploadingMedia || isSaving}
                    onChange={(e) => {
                      if (e.target.files?.length) handleQueueExtraMedia(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <span className="text-sm font-medium text-gray-600">+ Add images or documents</span>
                  <span className="text-xs text-gray-400">Uploaded to gallery only when you Save changes</span>
                </label>
                {uploadingMedia && (
                  <p className="text-xs text-blue-600 mt-1.5 font-medium">Uploading gallery…</p>
                )}
                {pendingGalleryFiles.length > 0 && !uploadingMedia && (
                  <p className="text-xs text-amber-600 mt-1.5 font-medium">
                    {pendingGalleryFiles.length} file{pendingGalleryFiles.length > 1 ? "s" : ""} queued — click “Save changes” to upload.
                  </p>
                )}
                {uploadMediaError && <p className="text-xs text-red-500 mt-1">{uploadMediaError}</p>}
              </div>
              )}

              {/* ── Title ───────────────────────────────────────────────── */}
              {editModalTab === "basic" && (<div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                />
                {editForm.title.length > 450 && (
                  <p className={`mt-0.5 text-xs text-right ${editForm.title.length >= 500 ? "text-red-500" : "text-amber-500"}`}>{editForm.title.length}/500</p>
                )}
                {editingItem.ai_suggested_title && (
                  <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                        AI Suggested Title
                      </p>
                      <span className={`text-xs font-medium ${editingItem.ai_used_suggested_title ? "text-emerald-700" : "text-amber-700"}`}>
                        {editingItem.ai_used_suggested_title ? "Applied" : "Not applied"}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-700">{editingItem.ai_suggested_title}</p>
                    {!editingItem.ai_used_suggested_title && (
                      <button
                        type="button"
                        onClick={() => void applyAiSuggestion("title")}
                        disabled={isSaving || isApplyingAiField !== null}
                        className="mt-3 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isApplyingAiField === "title" ? "Applying AI title..." : "Use AI title"}
                      </button>
                    )}
                  </div>
                )}
              </div>)}

              {/* ── Description ─────────────────────────────────────────── */}
              {editModalTab === "basic" && (<div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  maxLength={5000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none text-sm"
                />
                {editForm.description.length > 4500 && (
                  <p className={`mt-0.5 text-xs text-right ${editForm.description.length >= 5000 ? "text-red-500" : "text-amber-500"}`}>{editForm.description.length}/5000</p>
                )}
                {editingItem.ai_suggested_description && (
                  <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                        AI Suggested Description
                      </p>
                      <span className={`text-xs font-medium ${editingItem.ai_used_suggested_description ? "text-emerald-700" : "text-amber-700"}`}>
                        {editingItem.ai_used_suggested_description ? "Applied" : "Not applied"}
                      </span>
                    </div>
                    <p className="mt-1 whitespace-pre-line text-sm text-gray-700">
                      {editingItem.ai_suggested_description}
                    </p>
                    {!editingItem.ai_used_suggested_description && (
                      <button
                        type="button"
                        onClick={() => void applyAiSuggestion("description")}
                        disabled={isSaving || isApplyingAiField !== null}
                        className="mt-3 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isApplyingAiField === "description" ? "Applying AI description..." : "Use AI description"}
                      </button>
                    )}
                  </div>
                )}
              </div>)}

              {editModalTab === "basic" && locationText && (
                <div>
                  <p className="block text-sm font-semibold text-gray-700 mb-2">Extracted Locations</p>
                  <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                    {locationText}
                  </div>
                </div>
              )}

              {/* ── Pricing ───────────────────────────────────────────────── */}
              {editModalTab === "pricing" && (<>
              {/* Price inputs are replaced by a read-only notice when this deal's
                  category doesn't take pricing — otherwise a seller could type a
                  price that the save path silently discards, with no feedback. The
                  rest of this tab (travel dates, tags, validity periods) stays
                  editable. */}
              {editPricingExempt ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-600">
                Pricing is not required for this category — no price is stored for this deal.
              </div>
              ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700">Pricing ({currencySymbol(editForm.currency)})</label>
                  {pct !== null && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                      {pct}% OFF
                    </span>
                  )}
                </div>
                <div className="mb-3">
                  <label className="block text-xs text-gray-500 mb-1">Currency</label>
                  <select
                    value={editForm.currency}
                    onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white"
                  >
                    {currencyOptions.map((c) => (
                      <option key={c.code} value={c.code}>{c.symbol} — {c.code} ({c.label})</option>
                    ))}
                    {/* Keep the deal's saved currency selectable even if the
                        admin list no longer includes it. */}
                    {editForm.currency && !currencyOptions.some((c) => c.code === editForm.currency) && (
                      <option value={editForm.currency}>{currencySymbol(editForm.currency)} — {editForm.currency}</option>
                    )}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Original Price</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{currencySymbol(editForm.currency)}</span>
                      <input
                        type="number"
                        min="0"
                        value={editForm.original_price}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditForm((f) => ({
                            ...f,
                            original_price: v,
                            discounted_price: f.discounted_same_as_original ? v : f.discounted_price,
                          }));
                        }}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Discounted Price</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{currencySymbol(editForm.currency)}</span>
                      <input
                        type="number"
                        min="0"
                        value={editForm.discounted_same_as_original ? editForm.original_price : editForm.discounted_price}
                        disabled={editForm.discounted_same_as_original}
                        onChange={(e) => setEditForm({ ...editForm, discounted_price: e.target.value })}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm disabled:bg-gray-100 disabled:text-gray-400"
                      />
                    </div>
                  </div>
                </div>
                <label
                  htmlFor="edit-disc-same"
                  className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 cursor-pointer"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-700">Discounted price same as original</span>
                    <span className="block text-xs text-gray-500">
                      No separate offer price — buyers see the original price with no discount.
                    </span>
                  </span>
                  <input
                    id="edit-disc-same"
                    type="checkbox"
                    checked={editForm.discounted_same_as_original}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEditForm((f) => ({
                        ...f,
                        discounted_same_as_original: checked,
                        discounted_price: checked ? f.original_price : f.discounted_price,
                      }));
                    }}
                    className="h-5 w-5 shrink-0 accent-blue-600"
                  />
                </label>
              </div>
              )}

              {/* Individual Travel Dates */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Travel Dates ({editForm.dates.length} selected)
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {editForm.dates.map((d, di) => (
                    <span key={di} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      {d}
                      <button
                        type="button"
                        onClick={() => setEditForm((f) => ({ ...f, dates: f.dates.filter((_, i) => i !== di) }))}
                        className="ml-0.5 text-blue-500 hover:text-blue-800 font-bold leading-none"
                      >×</button>
                    </span>
                  ))}
                  {editForm.dates.length === 0 && <span className="text-xs text-gray-400 italic">No dates yet</span>}
                </div>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value=""
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && !editForm.dates.includes(v)) {
                      setEditForm((f) => ({ ...f, dates: [...f.dates, v].sort() }));
                    }
                    e.target.value = "";
                  }}
                />
                <p className="text-xs text-gray-400 mt-1">Pick a date to add it — repeat for each individual travel date</p>
              </div>

              {/* Tags */}
              <ChipsEditor
                label="Tags"
                value={editForm.tags}
                suggestions={availableTags}
                placeholder="Type a tag and press Enter (e.g. luxury, honeymoon)"
                onChange={(next) => setEditForm((f) => ({ ...f, tags: next }))}
              />

              {/* Destination */}
              <div className="grid grid-cols-2 gap-3">
                <ChipsEditor
                  label="Cities"
                  value={editForm.cities}
                  suggestions={availableCities}
                  placeholder="Type a city and press Enter (e.g. Goa)"
                  onChange={(next) => setEditForm((f) => ({ ...f, cities: next }))}
                />
                <ChipsEditor
                  label="States"
                  value={editForm.states}
                  suggestions={availableStates}
                  placeholder="Type a state/province and press Enter (e.g. Uttarakhand)"
                  onChange={(next) => setEditForm((f) => ({ ...f, states: next }))}
                />
                <ChipsEditor
                  label="Countries"
                  value={editForm.countries}
                  suggestions={availableCountries}
                  placeholder="Type a country and press Enter (e.g. India)"
                  onChange={(next) => setEditForm((f) => ({ ...f, countries: next }))}
                />
              </div>

              {/* Pax */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Min Pax</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                    value={editForm.min_pax}
                    onChange={(e) => setEditForm((f) => ({ ...f, min_pax: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Max Pax</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="20"
                    value={editForm.max_pax}
                    onChange={(e) => setEditForm((f) => ({ ...f, max_pax: e.target.value }))}
                  />
                </div>
              </div>

              {/* Terms */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Advance Booking</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 7 days"
                    value={editForm.advance_booking}
                    onChange={(e) => setEditForm((f) => ({ ...f, advance_booking: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Blackout Dates</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="2026-12-24, 2026-12-25"
                    value={editForm.blackout_dates}
                    onChange={(e) => setEditForm((f) => ({ ...f, blackout_dates: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cancellation Policy</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="e.g. Free cancellation up to 48 hours before check-in"
                  value={editForm.cancellation_policy}
                  onChange={(e) => setEditForm((f) => ({ ...f, cancellation_policy: e.target.value }))}
                />
              </div>

              {/* Inventory — seller-managed manual counts, never auto-decremented */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Total</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editForm.inventory_total}
                    onChange={(e) => setEditForm((f) => ({ ...f, inventory_total: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Remaining</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editForm.inventory_remaining}
                    onChange={(e) => setEditForm((f) => ({ ...f, inventory_remaining: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Cancelled</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editForm.inventory_cancelled}
                    onChange={(e) => setEditForm((f) => ({ ...f, inventory_cancelled: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Unit</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editForm.inventory_unit}
                    onChange={(e) => setEditForm((f) => ({ ...f, inventory_unit: e.target.value }))}
                  >
                    <option value="">—</option>
                    <option value="seats">Seats</option>
                    <option value="rooms">Rooms</option>
                    <option value="cabins">Cabins</option>
                    <option value="slots">Slots</option>
                  </select>
                </div>
              </div>

              {/* Curation — Deal-of-the-Hour / rail tags */}
              <div className="grid grid-cols-2 gap-3 items-end">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-is-flash"
                    checked={editForm.is_flash}
                    onChange={(e) => setEditForm((f) => ({ ...f, is_flash: e.target.checked }))}
                  />
                  <label htmlFor="edit-is-flash" className="text-xs font-medium text-gray-700">Flash Deal</label>
                </div>
                {editForm.is_flash && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Flash Ends At</label>
                    <input
                      type="datetime-local"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editForm.flash_ends_at}
                      onChange={(e) => setEditForm((f) => ({ ...f, flash_ends_at: e.target.value }))}
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Rail Tags</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="last_minute, trending, visa_free"
                  value={editForm.rail_tags}
                  onChange={(e) => setEditForm((f) => ({ ...f, rail_tags: e.target.value }))}
                />
              </div>

              {/* Inclusions */}
              <RowListEditor
                label="Inclusions"
                placeholder="e.g. Breakfast"
                items={editForm.inclusions}
                onChange={(items) => setEditForm((f) => ({ ...f, inclusions: items }))}
              />

              {/* Exclusions */}
              <RowListEditor
                label="Exclusions"
                placeholder="e.g. Flights"
                items={editForm.exclusions}
                onChange={(items) => setEditForm((f) => ({ ...f, exclusions: items }))}
              />

              {/* Highlights */}
              <RowListEditor
                label="Highlights"
                placeholder="e.g. 5-star resort"
                items={editForm.highlights}
                onChange={(items) => setEditForm((f) => ({ ...f, highlights: items }))}
              />

              {/* Validity Periods */}
              {editForm.validity_periods.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Date Batches / Validity Periods</label>
                  <div className="space-y-3">
                    {editForm.validity_periods.map((vp, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600">Batch {idx + 1}{vp.description ? ` — ${vp.description}` : ""}</span>
                          <button
                            type="button"
                            onClick={() => setEditForm((f) => ({ ...f, validity_periods: f.validity_periods.filter((_, i) => i !== idx) }))}
                            className="text-xs text-red-500 hover:text-red-700"
                          >Remove</button>
                        </div>
                        <div className="mb-2">
                          <label className="text-xs text-gray-500">Dates (comma-separated ISO)</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs mt-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            value={vp.dates?.join(", ") || ""}
                            onChange={(e) => {
                              const newDates = e.target.value.split(",").map(d => d.trim()).filter(Boolean);
                              setEditForm((f) => ({
                                ...f,
                                validity_periods: f.validity_periods.map((p, i) => i === idx ? { ...p, dates: newDates } : p),
                              }));
                            }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Price</label>
                            <input
                              type="number"
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs mt-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              value={vp.original_price ?? ""}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setEditForm((f) => ({
                                  ...f,
                                  validity_periods: f.validity_periods.map((p, i) => i === idx ? { ...p, original_price: isNaN(val) ? undefined : val } : p),
                                }));
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Discounted Price</label>
                            <input
                              type="number"
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs mt-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              value={vp.discounted_price ?? ""}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setEditForm((f) => ({
                                  ...f,
                                  validity_periods: f.validity_periods.map((p, i) => i === idx ? { ...p, discounted_price: isNaN(val) ? undefined : val } : p),
                                }));
                              }}
                            />
                          </div>
                        </div>
                        <div className="mt-2">
                          <label className="text-xs text-gray-500">Description / Label</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs mt-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            placeholder="e.g. Christmas batch, Peak season"
                            value={vp.description || ""}
                            onChange={(e) => setEditForm((f) => ({
                              ...f,
                              validity_periods: f.validity_periods.map((p, i) => i === idx ? { ...p, description: e.target.value } : p),
                            }))}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditForm((f) => ({ ...f, validity_periods: [...f.validity_periods, { dates: [], original_price: undefined, discounted_price: undefined }] }))}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >+ Add batch</button>
                </div>
              )}
              {editForm.validity_periods.length === 0 && (
                <button
                  type="button"
                  onClick={() => setEditForm((f) => ({ ...f, validity_periods: [{ dates: [], original_price: undefined, discounted_price: undefined }] }))}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >+ Add date batch with pricing</button>
              )}
              </>)}

              {/* ── Category-specific details ──────────────────────────────── */}
              {editModalTab === "category" && (() => {
                const group = ((editingItem.categories as Record<string, unknown> | undefined)?.group_slug as string | undefined) || editGroupSlug;
                if (group === "stays") {
                  return (
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Stay Details</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Property Name</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Taj Exotica Resort"
                            value={editForm.property_name}
                            onChange={(e) => setEditForm((f) => ({ ...f, property_name: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Property Type</label>
                          <select
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={editForm.property_type}
                            onChange={(e) => setEditForm((f) => ({ ...f, property_type: e.target.value }))}
                          >
                            <option value="">— Select —</option>
                            {STAY_PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                }
                if (group === "flights") {
                  return (
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Flight Details</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Airline</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="IndiGo"
                            value={editForm.airline_name}
                            onChange={(e) => setEditForm((f) => ({ ...f, airline_name: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Trip Type</label>
                          <select
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={editForm.trip_type}
                            onChange={(e) => setEditForm((f) => ({ ...f, trip_type: e.target.value }))}
                          >
                            <option value="">— Select —</option>
                            <option value="one_way">One Way</option>
                            <option value="round_trip">Round Trip</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                }
                if (group === "cruises") {
                  return (
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Cruise Details</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Ship Name</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="MSC Bellissima"
                            value={editForm.ship_name}
                            onChange={(e) => setEditForm((f) => ({ ...f, ship_name: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Cruise Line</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="MSC Cruises"
                            value={editForm.cruise_line}
                            onChange={(e) => setEditForm((f) => ({ ...f, cruise_line: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Embarkation Port</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Mumbai"
                            value={editForm.embarkation_port}
                            onChange={(e) => setEditForm((f) => ({ ...f, embarkation_port: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Disembarkation Port</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Goa"
                            value={editForm.disembarkation_port}
                            onChange={(e) => setEditForm((f) => ({ ...f, disembarkation_port: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
                if (group === "transfers" || group === "transport") {
                  return (
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Transfer Details</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle Type</label>
                          <select
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={editForm.vehicle_type}
                            onChange={(e) => setEditForm((f) => ({ ...f, vehicle_type: e.target.value }))}
                          >
                            <option value="">— Select —</option>
                            {TRANSFER_VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle Model</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Toyota Innova Crysta"
                            value={editForm.vehicle_model}
                            onChange={(e) => setEditForm((f) => ({ ...f, vehicle_model: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
                if (group === "experiences") {
                  return (
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Experience Details</p>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Experience Type</label>
                        <select
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={editForm.experience_type}
                          onChange={(e) => setEditForm((f) => ({ ...f, experience_type: e.target.value }))}
                        >
                          <option value="">— Select —</option>
                          {EXPERIENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                }
                if (group === "packages") {
                  return (
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Package Details</p>
                      <p className="text-xs text-gray-500">
                        Packages have no extra category-specific fields here — edit inclusions, exclusions and
                        highlights on the <strong>Basic</strong> tab, and pricing on the <strong>Pricing</strong> tab.
                      </p>
                    </div>
                  );
                }
                if (group === "services") {
                  return (
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Service Details</p>
                      <p className="text-xs text-gray-500">
                        Visa / insurance / custom service fields aren&rsquo;t editable from this dialog yet.
                        Title, description, tags and pricing can still be edited on the other tabs.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <p className="text-xs text-gray-500">
                      No category-specific fields for this deal type ({group || "unknown"}).
                    </p>
                  </div>
                );
              })()}

              {/* ── Slug / Deal Type / Package Settings ───────────────────── */}
              {editModalTab === "packaging" && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">🔗 Slugs &amp; Deal Type</p>

                {/* Deal type dropdown + primary slug */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Deal Type <span className="font-normal text-gray-400">(collection &amp; Pinecone namespace)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="px-3 py-2 border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-lg select-none">
                      {DEAL_TYPE_OPTIONS_INV.find((o) => o.value === editGroupSlug)?.label ?? editGroupSlug ?? "—"}
                    </div>
                    <span className="text-gray-400 text-xs font-mono">/</span>
                    {categoryLoadError ? (
                      <p className="flex-1 px-3 py-2 border border-red-300 bg-red-50 rounded-lg text-xs text-red-600 font-medium">{categoryLoadError}</p>
                    ) : (
                      <select
                        value={editPrimarySlug}
                        onChange={(e) => setEditPrimarySlug(e.target.value)}
                        disabled={categoryOptions.length === 0}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none bg-white disabled:opacity-50"
                      >
                        <option value="">{categoryOptions.length === 0 ? "Loading…" : "— pick primary slug —"}</option>
                        {categoryOptions
                          .filter((c) => !c.is_promotional && (!editGroupSlug || c.group_slug === editGroupSlug))
                          .map((c) => (
                            <option key={c.slug} value={c.slug}>{c.name} ({c.slug})</option>
                          ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Secondary slugs */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Secondary Slugs <span className="font-normal text-gray-400">(cross-category)</span>
                  </label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {editSecondarySlug.map((s, si) => (
                      <span key={si} className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        {s}
                        <button type="button" onClick={() => setEditSecondarySlugs((p) => p.filter((_, i) => i !== si))} className="ml-0.5 text-purple-500 hover:text-purple-800 font-bold leading-none">×</button>
                      </span>
                    ))}
                    {editSecondarySlug.length === 0 && <span className="text-xs text-gray-400 italic">None yet</span>}
                  </div>
                  <div className="flex gap-2">
                    {categoryLoadError ? (
                      <p className="flex-1 px-3 py-2 border border-red-300 bg-red-50 rounded-lg text-xs text-red-600 font-medium">{categoryLoadError}</p>
                    ) : (
                      <select
                        value=""
                        disabled={categoryOptions.length === 0}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v && !editSecondarySlug.includes(v)) setEditSecondarySlugs((p) => [...p, v]);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 outline-none bg-white disabled:opacity-50"
                      >
                        <option value="">{categoryOptions.length === 0 ? "Loading…" : "— add secondary slug —"}</option>
                        {categoryOptions
                          .filter((c) => !editSecondarySlug.includes(c.slug) && c.slug !== editPrimarySlug)
                          .map((c) => (
                            <option key={c.slug} value={c.slug}>{c.name} ({c.slug}){c.is_promotional ? " ★" : ""}</option>
                          ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Package toggle — hidden when deal already lives in deal_packages.
                    Prefer the server-derived flag; fall back to local derivation. */}
                {(editingItem.package_toggle_applicable
                    ?? (dealCollection(editingItem) !== "deal_packages")) && (
                  <>
                    <div className="flex items-start justify-between gap-4 pt-1 border-t border-gray-200">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-800">Also publish as Package</p>
                        <p className="text-xs text-gray-500 mt-0.5">Dual-index in packages Pinecone namespace.</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={editIsInPackages}
                        onClick={() => setEditIsInPackages((v) => !v)}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 mt-0.5 ${editIsInPackages ? "bg-indigo-600" : "bg-gray-300"}`}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-0 transition-transform duration-200 ${editIsInPackages ? "translate-x-5" : "translate-x-0"}`} />
                      </button>
                    </div>
                    {editIsInPackages && (
                      <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-3 space-y-3">
                        <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wide">Package Slug Settings</p>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Package Primary Slug</label>
                          <div className="flex items-center gap-1.5">
                            <span className="px-2 py-1.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-lg shrink-0">packages/</span>
                            {categoryLoadError ? (
                              <p className="flex-1 px-3 py-2 border border-red-300 bg-red-50 rounded-lg text-xs text-red-600 font-medium">{categoryLoadError}</p>
                            ) : (
                              <select
                                value={editPkgPrimarySlug}
                                onChange={(e) => setEditPkgPrimarySlug(e.target.value)}
                                disabled={categoryOptions.length === 0}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none bg-white disabled:opacity-50"
                              >
                                <option value="">{categoryOptions.length === 0 ? "Loading…" : "— pick package primary slug —"}</option>
                                {categoryOptions
                                  .filter((c) => !c.is_promotional && c.group_slug === "packages")
                                  .map((c) => (
                                    <option key={c.slug} value={c.slug}>{c.name} ({c.slug})</option>
                                  ))}
                              </select>
                            )}
                          </div>
                          {editPrimarySlug && editPrimarySlug !== editPkgPrimarySlug && (
                            <button type="button" onClick={() => setEditPkgPrimarySlug(editPrimarySlug)} className="mt-1 text-xs text-indigo-600 hover:underline">
                              ↩ Use main slug: {editPrimarySlug}
                            </button>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Package Secondary Slugs</label>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {editPkgSecondarySlugs.map((s, si) => (
                              <span key={si} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                                {s}
                                <button type="button" onClick={() => setEditPkgSecondarySlugs((p) => p.filter((_, i) => i !== si))} className="ml-0.5 text-indigo-500 hover:text-indigo-800 font-bold">×</button>
                              </span>
                            ))}
                            {editPkgSecondarySlugs.length === 0 && <span className="text-xs text-gray-400 italic">None yet</span>}
                          </div>
                          <div className="flex gap-2">
                            {categoryLoadError ? (
                              <p className="flex-1 px-3 py-2 border border-red-300 bg-red-50 rounded-lg text-xs text-red-600 font-medium">{categoryLoadError}</p>
                            ) : (
                              <select
                                value=""
                                disabled={categoryOptions.length === 0}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v && !editPkgSecondarySlugs.includes(v)) setEditPkgSecondarySlugs((p) => [...p, v]);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none bg-white disabled:opacity-50"
                              >
                                <option value="">{categoryOptions.length === 0 ? "Loading…" : "— add package secondary slug —"}</option>
                                {categoryOptions
                                  .filter((c) => c.group_slug === "packages" && !editPkgSecondarySlugs.includes(c.slug) && c.slug !== editPkgPrimarySlug)
                                  .map((c) => (
                                    <option key={c.slug} value={c.slug}>{c.name} ({c.slug}){c.is_promotional ? " ★" : ""}</option>
                                  ))}
                              </select>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

              </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="p-6 border-t border-gray-100 flex items-center justify-between gap-3">
              <button
                onClick={() => void handleDelete(editingItem)}
                disabled={isDeleting || isSaving}
                className="px-5 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
              <div className="flex items-center gap-3">
              <button
                onClick={closeEditModal}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Saving…" : pendingGalleryRemovals.length > 0 ? `Save & Remove ${pendingGalleryRemovals.length} file${pendingGalleryRemovals.length > 1 ? "s" : ""}` : "Save Changes"}
              </button>
              </div>
            </div>
          </div>
        </div>
          );
        })()
      )}
      {/* ── Publish blocked: destination not covered by seller's own declared markets ── */}
      {publishBlockedError && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <h3 className="text-base font-bold text-gray-900 mb-1">
              {publishBlockedError.code === "market_pending_review"
                ? "Market pending review"
                : publishBlockedError.code === "last_minute_pending_review"
                ? "Pending admin approval"
                : publishBlockedError.code === "dates_expired_pending_review"
                ? "Travel dates have already passed"
                : "Can't publish this deal yet"}
            </h3>
            {(() => {
              // Backend sends one plain sentence: "<intro>. <dest1> • <dest2> … <trailer>"
              // — destinations are only "•"-joined from each other, not from the intro or
              // trailer, and with a single destination there's no "•" at all. Pull the
              // trailer off by content match first, then split the intro from the
              // destination list on the "yet." boundary.
              const raw = publishBlockedError.message;
              const trailerMatch = raw.match(/Update your profile[\s\S]*$/i);
              const trailer = trailerMatch?.[0]?.trim() ?? null;
              const withoutTrailer = trailer ? raw.slice(0, trailerMatch!.index).trim() : raw;

              const introMatch = withoutTrailer.match(/^[\s\S]*?\byet\.?/i);
              const intro = (introMatch?.[0] ?? withoutTrailer).trim();
              const destinationsPart = introMatch ? withoutTrailer.slice(introMatch[0].length).trim() : "";
              const items = destinationsPart.split("•").map((s) => s.trim()).filter(Boolean);

              if (items.length === 0) {
                return <p className="text-sm text-gray-700 leading-snug mb-2">{raw}</p>;
              }
              return (
                <div className="mb-2">
                  <p className="text-sm text-gray-700 leading-snug mb-2">{intro}</p>
                  <ul className="list-disc pl-5 space-y-1 max-h-40 overflow-y-auto">
                    {items.map((item, i) => (
                      <li key={i} className="text-sm font-semibold text-amber-700">{item}</li>
                    ))}
                  </ul>
                  {trailer && <p className="text-sm text-gray-700 leading-snug mt-2">{trailer}</p>}
                </div>
              );
            })()}
            {publishBlockedError.pending_countries && publishBlockedError.pending_countries.length > 0 && (
              <p className="text-xs text-amber-700 mb-3">
                Pending: {publishBlockedError.pending_countries.map((code) => countryNameByIso2.get(code) ?? code).join(", ")}
              </p>
            )}
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setPublishBlockedError(null)}
                className="flex-1 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              {publishBlockedError.code === "market_not_covered" && (
                <button
                  onClick={() => {
                    const stepKey = publishBlockedError.onboarding_step_key;
                    setPublishBlockedError(null);
                    navigate("/profile", {
                      state: { resumeStepKey: stepKey },
                    });
                  }}
                  className="flex-1 py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700"
                >
                  Update Profile →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Deal delete confirmation ── */}
      {confirmDeleteDeal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-base font-bold text-gray-900 mb-1">Delete this deal?</h3>
            <p className="text-sm text-gray-700 font-medium mb-1">{confirmDeleteDeal.title}</p>
            <p className="text-xs text-red-600 mb-5">This permanently removes the deal, all media from storage, and its search index. Cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteDeal(null)} className="flex-1 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => void confirmAndDelete(confirmDeleteDeal)} disabled={isDeleting} className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                {isDeleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Gallery removal confirm (shown before save when items are pending) ── */}
      {showGalleryDeleteConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-base font-bold text-gray-900 mb-1">Delete media permanently?</h3>
            <p className="text-sm text-gray-600 mb-1">
              {pendingGalleryRemovals.length} file{pendingGalleryRemovals.length > 1 ? "s" : ""} will be permanently removed from storage and cannot be recovered.
            </p>
            <p className="text-xs text-gray-400 mb-5">All other changes will also be saved.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowGalleryDeleteConfirm(false)} className="flex-1 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => void handleSave()} className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700">Delete &amp; Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── News Edit Modal ────────────────────────────────────────────────────────────

const NEWS_CATEGORIES_OPTS = [
  "general","airline","hotel","destination","visa","regulatory","industry","technology","cruise","deals",
];

function NewsEditModal({
  article,
  saving,
  error,
  onSave,
  onClose,
}: {
  article: NewsArticle;
  saving: boolean;
  error: string;
  onSave: (id: string, fd: FormData) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = React.useState(article.title ?? "");
  const [summary, setSummary] = React.useState(article.summary ?? "");
  const [body, setBody] = React.useState(article.body ?? "");
  const [category, setCategory] = React.useState(article.category ?? "general");
  const [tags, setTags] = React.useState((article.tags ?? []).join(", "));
  const [destinations, setDestinations] = React.useState((article.destinations ?? []).join(", "));
  const [source, setSource] = React.useState(article.source ?? "");
  const [sourceUrl, setSourceUrl] = React.useState(article.source_url ?? "");
  const [sourceAuthor, setSourceAuthor] = React.useState(article.source_author ?? "");
  const [coverFile, setCoverFile] = React.useState<File | null>(null);
  const [coverPreview, setCoverPreview] = React.useState<string | null>(null);
  const [newGalleryFiles, setNewGalleryFiles] = React.useState<File[]>([]);
  const [newAttachFiles, setNewAttachFiles] = React.useState<File[]>([]);
  const galleryRef = React.useRef<HTMLInputElement>(null);
  const attachRef = React.useRef<HTMLInputElement>(null);

  useBodyScrollLock();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const fd = new FormData();
    fd.append("title", title.trim() || "Untitled");
    fd.append("summary", summary);
    fd.append("body", body);
    fd.append("category", category);
    fd.append("tags", tags);
    fd.append("destinations", destinations);
    fd.append("source", source);
    fd.append("source_url", sourceUrl);
    fd.append("source_author", sourceAuthor);
    if (coverFile) fd.append("cover_image", coverFile, coverFile.name);
    for (const f of newGalleryFiles) fd.append("gallery", f, f.name);
    for (const f of newAttachFiles) fd.append("attachments", f, f.name);
    onSave(article.id, fd);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-2 sm:px-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">Edit Article</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form id="news-edit-form" onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

          {/* Current cover */}
          {(coverPreview || article.cover_image_url) && (
            <div className="relative rounded-xl overflow-hidden border border-gray-200 h-36">
              <img src={coverPreview ?? article.cover_image_url ?? ""} alt="cover" className="w-full h-full object-cover" />
              {coverPreview && (
                <button type="button" onClick={() => { setCoverFile(null); setCoverPreview(null); }}
                  className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow text-gray-600 hover:bg-white">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Replace Cover Image</label>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="text-xs text-gray-600 w-full"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); }
                e.target.value = "";
              }} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Summary</label>
            <textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={1000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Body</label>
            <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} maxLength={50000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white">
                {NEWS_CATEGORIES_OPTS.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Source</label>
              <input type="text" value={source} onChange={(e) => setSource(e.target.value)} maxLength={300}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Author</label>
              <input type="text" value={sourceAuthor} onChange={(e) => setSourceAuthor(e.target.value)} maxLength={300}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">News Link</label>
              <input type="text" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} maxLength={1000}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Tags <span className="font-normal text-gray-400">comma-separated</span></label>
            <input type="text" value={tags} onChange={(e) => setTags(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Destinations <span className="font-normal text-gray-400">comma-separated</span></label>
            <input type="text" value={destinations} onChange={(e) => setDestinations(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>

          {/* Existing gallery */}
          {article.gallery && article.gallery.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Existing Gallery ({article.gallery.length})</p>
              <div className="flex gap-1.5 flex-wrap">
                {article.gallery.map((g, i) => <img key={i} src={g.url} alt="" className="w-14 h-14 rounded-lg object-cover border border-gray-200" />)}
              </div>
            </div>
          )}

          {/* Add more gallery images */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Add Gallery Images</label>
            <div className="flex flex-wrap gap-1.5 mb-1">
              {newGalleryFiles.map((f, i) => (
                <div key={i} className="relative w-12 h-12 rounded-lg overflow-hidden border border-gray-200 group">
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setNewGalleryFiles((p) => p.filter((_, j) => j !== i))}
                    className="absolute inset-0 bg-black/40 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100">✕</button>
                </div>
              ))}
              <label className="w-12 h-12 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-blue-400 text-gray-400 text-xl">
                +
                <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden"
                  onChange={(e) => { setNewGalleryFiles((p) => [...p, ...Array.from(e.target.files ?? [])]); e.target.value = ""; }} />
              </label>
            </div>
          </div>

          {/* Existing attachments */}
          {article.attachments && article.attachments.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Existing Attachments</p>
              <div className="space-y-0.5">
                {article.attachments.map((att, i) => (
                  <AttachmentLink
                    key={i}
                    attachment={att}
                    className="flex items-center gap-2 text-xs text-blue-600 hover:underline"
                    iconClassName="w-8 h-8 object-cover rounded border border-gray-200 shrink-0"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Add more attachments */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Add Attachments</label>
            <div className="space-y-1 mb-1">
              {newAttachFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs">
                  <span>{f.name.endsWith(".pdf") ? "📄" : "📝"}</span>
                  <span className="flex-1 truncate text-gray-700">{f.name}</span>
                  <button type="button" onClick={() => setNewAttachFiles((p) => p.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">✕</button>
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 w-fit text-xs text-gray-600">
              📎 Attach PDF / DOCX
              <input ref={attachRef} type="file" accept=".pdf,.doc,.docx" multiple className="hidden"
                onChange={(e) => { setNewAttachFiles((p) => [...p, ...Array.from(e.target.files ?? [])]); e.target.value = ""; }} />
            </label>
          </div>

          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">{error}</div>}
        </form>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 border-t border-gray-200 flex-shrink-0">
          <button type="button" onClick={onClose} disabled={saving}
            className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" form="news-edit-form" disabled={saving}
            className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? (
              <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>Saving…</>
            ) : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MyInventoryPage;
